import { useState, useEffect, useMemo, useRef } from 'react';
import { Search, Star, Users, Building, MessageSquare, X, Phone, Wallet, History as HistoryIcon, RotateCcw, ChevronLeft, ChevronRight, ChevronDown, MapPin, UserMinus, Calendar, Plus, Trash2, ArchiveRestore, Clock, ShieldCheck, CheckCircle2, Receipt, Send, Copy, Download, ExternalLink, Check, RefreshCw, Table, LayoutGrid, ArrowUpDown, ArrowUp, ArrowDown, Eye, Info, HeartPulse, ClipboardList, Sparkles, ChevronUp, Save, Loader2, MessageCircle, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import ClientDetailsModal from './components/ClientDetailsModal';
import BulkMessageModal from './components/BulkMessageModal';
import { restartClientService } from '../services/serviceLifecycle';
import { generateAndUploadInvoicePdf } from '../utils/generateInvoicePdf';
import { extractCity } from '../utils/crm';
import { useAuth } from '../contexts/AuthContext';
import RequestDeletionModal from '../components/common/RequestDeletionModal';
import { buildConsentFlowActionData, buildBabyCareFlowActionData, buildPatientCareFlowActionData } from '../utils/consentFlow';
import { maskPhoneNumber } from '../utils/phoneMask';
import { logClientExport, logAuditEvent } from '../services/auditService';

const PATIENT_CARE_DUTY_LABELS: Record<string, string> = {
    sponge_bath: 'Sponge Bath',
    bed_ridden: 'Bed Ridden Patient',
    exercise: 'Exercise Assistance',
    walking: 'Walking Assistance',
    diaper_change: 'Diaper Change',
    patient_cleanliness: 'Patient Cleanliness',
    feeding: 'Patient Feeding',
    medication: 'Medication Assistance',
    bp_check: 'BP & Vital Check',
    sugar_check: 'Sugar Check',
    nebulization: 'Nebulization',
    feeding_tube: 'Feeding Tube Care',
    feed_rt_tube: 'RT Tube Feeding',
    urine_bag_change: 'Urine Bag Change',
    urine_bag: 'Urine Bag Care',
    dressing: 'Wound Dressing',
    pateint_bath: 'Patient Bath',
    clean_room: 'Patient Room Cleaning',
    changing_cloths: 'Changing Clothes',
    change_bedshit: 'Change Bed Sheet',
    cut_nails: 'Cut Nails',
    take_in_washroom: 'Washroom Assistance',
    regular_checkup: 'Regular Checkups',
    massage: 'Body Massage',
    other: 'Other Patient Care',
};

const BABY_CARE_DUTY_LABELS: Record<string, string> = {
    baby_massage: 'Baby Massage',
    baby_bath: 'Baby Bath',
    diaper_change: 'Diaper Change',
    baby_feeding: 'Baby Feeding & Burping',
    baby_clothes_wash: 'Baby Clothes Washing',
    baby_clothes_iron: 'Baby Clothes Ironing',
    baby_vessels_wash: 'Baby Vessels Sterilizing',
    baby_room_clean: 'Baby Room Cleaning',
    mother_care: 'Mother Care Assistance',
    mother_food_prep: 'Mother Food Preparation',
    mother_clothes_wash: 'Mother Clothes Washing',
    other: 'Other Baby Care Work',
};

const getRestartDutyLabel = (dutyId: string, formType?: string): string => {
    const map = formType === 'baby_care_form' ? BABY_CARE_DUTY_LABELS : PATIENT_CARE_DUTY_LABELS;
    return map[dutyId] || dutyId.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

function parseAgeAndWeight(
    rawAge?: string | number | null,
    rawUnit?: string | null,
    rawWeight?: string | number | null,
    isBabyDefault?: boolean
): { age: string; ageUnit: 'Days' | 'Months' | 'Years'; weight: string } {
    let ageStr = String(rawAge ?? '').trim();
    let weightStr = String(rawWeight ?? '').trim();
    let unit: 'Days' | 'Months' | 'Years' = isBabyDefault ? 'Days' : 'Years';

    const unitLower = String(rawUnit || '').trim().toLowerCase();
    if (unitLower.includes('day') || unitLower === 'd') {
        unit = 'Days';
    } else if (unitLower.includes('month') || unitLower === 'mo') {
        unit = 'Months';
    } else if (unitLower.includes('year') || unitLower === 'yr') {
        unit = 'Years';
    }

    if (ageStr) {
        // Detect weight if embedded in age string (e.g. "50 years & 45kgs", "50 yrs, 45 kg")
        const weightMatch = ageStr.match(/(\d+(?:\.\d+)?)\s*(?:kgs?|kilos?|kg\b)/i);
        if (weightMatch) {
            if (!weightStr) {
                weightStr = weightMatch[1];
            }
            ageStr = ageStr.replace(/(?:&|\band\b|,|\/|\+)?\s*\d+(?:\.\d+)?\s*(?:kgs?|kilos?|kg\b)/gi, '').trim();
        }

        // Detect unit in age string if present
        if (/\b(?:days?|d)\b/i.test(ageStr)) {
            unit = 'Days';
        } else if (/\b(?:months?|m|mo)\b/i.test(ageStr)) {
            unit = 'Months';
        } else if (/\b(?:years?|yrs?|y)\b/i.test(ageStr)) {
            unit = 'Years';
        }

        // Strip words like "years", "months", "days" so the numerical or core age remains
        const cleanAgeNum = ageStr.replace(/\b(?:years?|yrs?|months?|mo|days?|d)\b/gi, '').trim();
        if (cleanAgeNum) {
            ageStr = cleanAgeNum;
        }
    }

    if (weightStr) {
        weightStr = weightStr.replace(/\s*(?:kgs?|kg|kilos?)\b/gi, '').trim();
    }

    return { age: ageStr, ageUnit: unit, weight: weightStr };
}

const renderAgeWeightPreview = (age: string, unit: string, weight: string) => {
    if (!age && !weight) return 'Age: N/A';
    let ageDisplay = '';
    if (age) {
        const lower = age.toLowerCase();
        if (lower.includes('year') || lower.includes('month') || lower.includes('day')) {
            ageDisplay = age;
        } else {
            ageDisplay = `${age} ${unit}`;
        }
    }
    const cleanWeight = weight ? weight.replace(/\s*(?:kgs?|kg)\b/gi, '').trim() : '';
    const weightDisplay = cleanWeight ? `${cleanWeight}kg` : '';
    if (ageDisplay && weightDisplay) return `${ageDisplay} • ${weightDisplay}`;
    return ageDisplay || weightDisplay;
};

const GOOGLE_PLACE_ID = 'ChIJnbC9IuxN4DsRXEWEnUc0HF8';
const GOOGLE_REVIEW_URL = `https://search.google.com/local/writereview?placeid=${GOOGLE_PLACE_ID}`;
const GOOGLE_MAPS_URL = `https://www.google.com/maps/place/?q=place_id:${GOOGLE_PLACE_ID}`;

export default function Clients() {
    const { user, canViewPhoneNumbers } = useAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const restartClientId = searchParams.get('restart');
    const [clients, setClients] = useState<any[]>([]);

    const [workflows, setWorkflows] = useState({
        reviewCollection: true,
    });


    const [inspectingClient, setInspectingClient] = useState<any>(null);

    // Search and View All filters
    const [searchQuery, setSearchQuery] = useState('');
    const [viewAllMonths, setViewAllMonths] = useState(true);

    // View Mode & Sorting State (Issue 1)
    const [viewMode, setViewMode] = useState<'table' | 'cards'>(() => {
        try {
            return (localStorage.getItem('99care_clients_view_mode') as 'table' | 'cards') || 'table';
        } catch {
            return 'table';
        }
    });

    const toggleViewMode = (mode: 'table' | 'cards') => {
        setViewMode(mode);
        try {
            localStorage.setItem('99care_clients_view_mode', mode);
        } catch {}
    };

    const [sortField, setSortField] = useState<'displayId' | 'name' | 'city' | 'phone' | 'workerCount' | 'securityDeposit' | 'status' | 'created_at'>('displayId');
    const [sortAsc, setSortAsc] = useState<boolean>(true);
    const [copiedClientIdBadge, setCopiedClientIdBadge] = useState<string | null>(null);
    const [copiedPhoneBadge, setCopiedPhoneBadge] = useState<string | null>(null);

    const handleSort = (field: typeof sortField) => {
        if (sortField === field) {
            setSortAsc(!sortAsc);
        } else {
            setSortField(field);
            setSortAsc(true);
        }
    };

    const handleCopyClientId = (idToCopy: string, e: React.MouseEvent) => {
        e.stopPropagation();
        navigator.clipboard.writeText(idToCopy);
        setCopiedClientIdBadge(idToCopy);
        toast.success(`Copied ID: ${idToCopy}`);
        setTimeout(() => setCopiedClientIdBadge(null), 2000);
    };

    const handleCopyPhone = (phoneToCopy: string, e: React.MouseEvent) => {
        e.stopPropagation();
        const clean = phoneToCopy.trim();
        const textToCopy = canViewPhoneNumbers ? clean : maskPhoneNumber(clean, false);
        navigator.clipboard.writeText(textToCopy);
        setCopiedPhoneBadge(clean);
        toast.success(`Copied phone: ${textToCopy}`);
        setTimeout(() => setCopiedPhoneBadge(null), 2000);
    };

    // City filter state
    const [selectedCity, setSelectedCity] = useState<string>('all');
    const [isCityDropdownOpen, setIsCityDropdownOpen] = useState<boolean>(false);

    // Monthly slider state
    const [selectedMonth, setSelectedMonth] = useState<string>(() => {
        const now = new Date();
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    });

    // Restart Service modal state
    const [restartModal, setRestartModal] = useState<any>(null);
    const [restartServiceType, setRestartServiceType] = useState('Old Age Care');
    const [restartShiftHours, setRestartShiftHours] = useState(10);
    const [restartStartDate, setRestartStartDate] = useState('');
    const [restartEndDate, setRestartEndDate] = useState('');
    const [restartIsOngoing, setRestartIsOngoing] = useState(true);
    const [restartCompleteRate, setRestartCompleteRate] = useState<number | ''>(800);
    const [restartIncompleteRate, setRestartIncompleteRate] = useState<number | ''>(1500);
    const [restartDepositAmount, setRestartDepositAmount] = useState<number | ''>(5000);
    const [restartDepositStatus, setRestartDepositStatus] = useState<'collected' | 'pending'>('pending');
    const [restartDepositMethod, setRestartDepositMethod] = useState('UPI');
    const [restartWorkers, setRestartWorkers] = useState<any[]>([]);
    const [restartWorkerSearch, setRestartWorkerSearch] = useState('');
    const [restartSelectedWorkers, setRestartSelectedWorkers] = useState<any[]>([]);
    const [restartNotes, setRestartNotes] = useState('');
    const [isRestartSubmitting, setIsRestartSubmitting] = useState(false);

    // Patient Care & Work Form Scope state for Restart Service
    const [restartLeadFull, setRestartLeadFull] = useState<any>(null);
    const [restartPatientName, setRestartPatientName] = useState('');
    const [restartAge, setRestartAge] = useState('');
    const [restartAgeUnit, setRestartAgeUnit] = useState<'Days' | 'Months' | 'Years'>('Years');
    const [restartWeight, setRestartWeight] = useState('');
    const [restartRelativeName, setRestartRelativeName] = useState('');
    const [restartContactNumber, setRestartContactNumber] = useState('');
    const [restartAltContactNumber, setRestartAltContactNumber] = useState('');
    const [restartAddress, setRestartAddress] = useState('');
    const [restartMedicalCondition, setRestartMedicalCondition] = useState('');
    const [restartFormType, setRestartFormType] = useState<'baby_care_form' | 'patient_care_form'>('patient_care_form');
    const [restartDuties, setRestartDuties] = useState<string[]>([]);
    const [restartCareInstructions, setRestartCareInstructions] = useState('');
    const [isManualCareExpanded, setIsManualCareExpanded] = useState(false);
    const [isSendingConsent, setIsSendingConsent] = useState(false);
    const [isSendingWorkForm, setIsSendingWorkForm] = useState(false);
    const [isSavingCareDetails, setIsSavingCareDetails] = useState(false);

    const toggleRestartDuty = (dutyKey: string) => {
        setRestartDuties(prev =>
            prev.includes(dutyKey) ? prev.filter(k => k !== dutyKey) : [...prev, dutyKey]
        );
    };

    const selectAllRestartDuties = () => {
        const dict = restartFormType === 'baby_care_form' ? BABY_CARE_DUTY_LABELS : PATIENT_CARE_DUTY_LABELS;
        setRestartDuties(Object.keys(dict));
    };

    const clearAllRestartDuties = () => {
        setRestartDuties([]);
    };

    const toggleRestartWorker = (w: any) => {
        setRestartSelectedWorkers(prev => {
            const exists = prev.some(item => item.id === w.id);
            if (exists) return prev.filter(item => item.id !== w.id);

            // Guard: Prevent selecting worker who is already deployed to another client
            if (w.status && w.status.toLowerCase() === 'assigned') {
                toast.error(`${w.full_name || 'Worker'} is currently deployed to ${w.assigned_client || 'another client'}. Please release them before reassigning.`);
                return prev;
            }

            return [...prev, w];
        });
    };

    // Deposit Invoice & Payment Modal State
    const [depositModal, setDepositModal] = useState<{
        client: any;
        depositAmount: number;
        depositStatus: 'collected' | 'pending' | 'settled';
        serviceName: string;
        startDate: string;
        allCycles: any[];
        pastDepositTotal: number;
        hasActiveService?: boolean;
    } | null>(null);
    const [depositPaymentMethod, setDepositPaymentMethod] = useState('UPI');
    const [depositPaymentAmount, setDepositPaymentAmount] = useState<number | ''>(5000);
    const [depositPaymentDate, setDepositPaymentDate] = useState<string>(() => {
        const d = new Date();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    });
    const [depositPaymentRef, setDepositPaymentRef] = useState('');
    const [isRecordingPayment, setIsRecordingPayment] = useState(false);
    const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
    const [generatedInvoiceUrl, setGeneratedInvoiceUrl] = useState<string | null>(null);
    const [copiedInvoiceLink, setCopiedInvoiceLink] = useState(false);

    const generateDepositRef = (method: string) => {
        const cleanMethod = (method || 'UPI').toUpperCase().replace(/\s+/g, '-');
        const randomPart = crypto.randomUUID().replace(/-/g, '').substring(0, 8).toUpperCase();
        return `${cleanMethod}-${randomPart}`;
    };

    const openDepositModal = (client: any) => {
        const activeSvc = client.activeService;
        const currentCycle = client.allServiceCycles?.find((c: any) => c.isCurrent);
        const latestCycle = client.allServiceCycles?.[0];
        const hasActiveService = Boolean(activeSvc || client.activeAssignment || currentCycle);
        const serviceName = currentCycle?.serviceType || (activeSvc?.service_type && activeSvc.service_type !== 'date_range' && activeSvc.service_type !== 'open_ended' ? activeSvc.service_type : (latestCycle?.serviceType || 'Home Care Service'));
        const startDate = activeSvc?.start_date || currentCycle?.startDate || latestCycle?.startDate || new Date().toISOString().split('T')[0];
        const amount = hasActiveService ? (client.securityDeposit || currentCycle?.depositAmount || 5000) : 0;
        const depositStatus = hasActiveService ? (client.depositStatus || 'pending') : 'settled';
        const initialMethod = 'UPI';

        setDepositModal({
            client,
            depositAmount: amount,
            depositStatus: depositStatus,
            serviceName,
            startDate,
            allCycles: client.allServiceCycles || [],
            pastDepositTotal: client.pastDepositTotal || 0,
            hasActiveService,
        });
        setDepositPaymentAmount(amount);
        setDepositPaymentMethod(initialMethod);
        setDepositPaymentRef(generateDepositRef(initialMethod));
        const d = new Date();
        setDepositPaymentDate(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`);
        setGeneratedInvoiceUrl(null);
        setCopiedInvoiceLink(false);
    };

    const handleShareDepositWhatsApp = (client: any, amount: number, serviceName: string, startDate: string, pdfUrl?: string) => {
        const rawPhone = (client.phone || '').replace(/\D/g, '');
        const cleanPhone = rawPhone.startsWith('91') ? rawPhone : `91${rawPhone}`;

        let msg = `Namaste ${client.name} ji! 🙏\n\nAapka 99 Care security deposit invoice ready hai.\n\n💰 *Security Deposit Amount*: ₹${amount.toLocaleString('en-IN')}\n💼 *Service*: ${serviceName}\n📅 *Start Date*: ${startDate}\n\n*Payment Details:*\n• UPI ID: 99care@upi\n• Google Pay / PhonePe / Paytm: 99care@upi\n• Bank: 99 Care Healthcare Services\n  A/C: 921020048192841\n  IFSC: UTIB0000160\n  Axis Bank, Surat\n`;

        if (pdfUrl) {
            msg += `\n📄 *Invoice PDF*: ${pdfUrl}\n`;
        }

        msg += `\nPayment complete karne ke baad kripya yahan transaction screenshot share karein taaki humari team verify kar sake.\n\nDhanyawad,\n99 Care Support Team`;

        const waUrl = `https://wa.me/${cleanPhone}?text=${encodeURIComponent(msg)}`;
        window.open(waUrl, '_blank');
    };

    const handleShareDepositReceiptWhatsApp = (client: any, amount: number, method: string, ref: string, serviceName: string, paymentDate?: string) => {
        const rawPhone = (client.phone || '').replace(/\D/g, '');
        const cleanPhone = rawPhone.startsWith('91') ? rawPhone : `91${rawPhone}`;
        const dateObj = paymentDate ? new Date(paymentDate.includes('T') ? paymentDate : `${paymentDate}T12:00:00`) : new Date();
        const formattedDate = dateObj.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });

        const msg = `Namaste ${client.name} ji! 🙏\n\nHumne aapka 99 Care security deposit payment successfully receive kar liya hai.\n\n💰 *Amount Received*: ₹${amount.toLocaleString('en-IN')}\n💳 *Payment Mode*: ${method}\n🔖 *Reference ID*: ${ref}\n💼 *Service*: ${serviceName}\n📅 *Date*: ${formattedDate}\n\nAapki care service smoothly active hai. Kisi bhi sahayata ke liye humse sampark karein.\n\nDhanyawad,\n99 Care Support Team`;

        const waUrl = `https://wa.me/${cleanPhone}?text=${encodeURIComponent(msg)}`;
        window.open(waUrl, '_blank');
    };

    const handleGenerateDepositPdf = async () => {
        if (!depositModal) return;
        setIsGeneratingPdf(true);
        try {
            const invNum = `DEP-${Math.floor(100000 + Math.random() * 900000)}`;
            const pdfUrl = await generateAndUploadInvoicePdf({
                clientId: depositModal.client.id,
                clientName: depositModal.client.name,
                clientPhone: depositModal.client.phone,
                invoiceNumber: invNum,
                invoiceDate: new Date().toISOString().split('T')[0],
                dueDate: new Date(Date.now() + 86400000).toISOString().split('T')[0],
                serviceName: `Security Deposit — ${depositModal.serviceName}`,
                servicePeriod: `${depositModal.startDate} to Ongoing`,
                days: 1,
                ratePerDay: Number(depositModal.depositAmount),
                grossAmount: Number(depositModal.depositAmount),
                previouslyBilled: 0,
                depositCollected: 0,
                settlementAmount: Number(depositModal.depositAmount),
                isFinalSettlement: false,
            });

            setGeneratedInvoiceUrl(pdfUrl);
            toast.success('Deposit invoice PDF generated successfully!');
        } catch (err: any) {
            console.error('Error generating deposit invoice PDF:', err);
            toast.error(`Failed to generate PDF: ${err.message || 'Error'}`);
        } finally {
            setIsGeneratingPdf(false);
        }
    };

    const handleRecordDepositPayment = async () => {
        if (!depositModal) return;
        const amount = Number(depositPaymentAmount) || depositModal.depositAmount;
        if (amount <= 0) return toast.error('Please enter a valid deposit amount.');

        const finalRef = depositPaymentRef.trim() || generateDepositRef(depositPaymentMethod);
        setIsRecordingPayment(true);
        try {
            const client = depositModal.client;
            const nowIso = new Date().toISOString();
            const paymentDateIso = depositPaymentDate
                ? (depositPaymentDate.includes('T') ? new Date(depositPaymentDate).toISOString() : new Date(`${depositPaymentDate}T12:00:00`).toISOString())
                : nowIso;

            // 1. Update active service
            await supabase
                .from('services')
                .update({
                    deposit_status: 'collected',
                    deposit_amount: amount,
                    updated_at: nowIso,
                })
                .eq('client_id', client.id)
                .eq('status', 'active');

            // 2. Update active worker_assignments
            await supabase
                .from('worker_assignments')
                .update({
                    deposit_paid: amount,
                    deposit_amount: amount,
                    deposit_invoice_sent: true,
                })
                .eq('client_id', client.id)
                .eq('assignment_status', 'active');

            // 3. Insert record in payments table (connected to Global Payment History & Finance)
            const { error: insertPayError } = await supabase.from('payments').insert([{
                client_name: client.name,
                amount: amount,
                payment_date: paymentDateIso,
                payment_type: 'deposit',
                recorded_by: 'admin',
                transaction_ref: finalRef,
            }]);

            if (insertPayError) throw insertPayError;

            // 4. Ensure lead is in Active Client and sync service role
            await supabase.from('crm_leads').update({
                pipeline_stage: 'Active Client',
                assigned_worker_role: depositModal.serviceName || undefined,
                updated_at: nowIso,
            }).eq('id', client.id);

            // 5. Connect Activity Log in crm_lead_activity
            await supabase.from('crm_lead_activity').insert([{
                lead_id: client.id,
                event_type: 'payment_recorded',
                description: `Deposit collection recorded: ₹${amount.toLocaleString('en-IN')} via ${depositPaymentMethod} (Ref: ${finalRef}, Date: ${depositPaymentDate})`,
                created_at: paymentDateIso,
                metadata: {
                    amount,
                    payment_type: 'deposit',
                    payment_method: depositPaymentMethod,
                    transaction_ref: finalRef,
                    payment_date: paymentDateIso,
                }
            }]);

            toast.success(`Deposit payment of ₹${amount.toLocaleString('en-IN')} recorded for ${client.name}! 🎉`, {
                action: {
                    label: 'WhatsApp Receipt',
                    onClick: () => handleShareDepositReceiptWhatsApp(client, amount, depositPaymentMethod, finalRef, depositModal.serviceName, depositPaymentDate),
                },
                duration: 6000,
            });

            setDepositModal(null);
            fetchClients();
        } catch (err: any) {
            console.error('Failed to record deposit payment:', err);
            toast.error(`Failed to record deposit payment: ${err.message || 'Error'}`);
        } finally {
            setIsRecordingPayment(false);
        }
    };

    // Track which clients have had review sent this session
    const [reviewSentIds, setReviewSentIds] = useState<Set<string>>(new Set());
    const [googleReviews, setGoogleReviews] = useState<any>(null);
    const [isLoadingGoogleReviews, setIsLoadingGoogleReviews] = useState(false);

    const toggleWorkflow = (key: keyof typeof workflows) => {
        setWorkflows(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const monthLabel = (m: string) => {
        const [y, mo] = m.split('-');
        return new Date(Number(y), Number(mo) - 1, 1).toLocaleString('default', { month: 'long', year: 'numeric' });
    };

    const formatDateDisplay = (dateStr?: string | null) => {
        if (!dateStr) return '';
        try {
            const clean = dateStr.includes('T') ? dateStr.split('T')[0] : dateStr;
            const parts = clean.split('-').map(Number);
            if (parts.length === 3 && parts[0] && parts[1] && parts[2]) {
                const dateObj = new Date(parts[0], parts[1] - 1, parts[2]);
                return dateObj.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
            }
            const dateObj = new Date(dateStr);
            if (!isNaN(dateObj.getTime())) {
                return dateObj.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
            }
            return dateStr;
        } catch {
            return dateStr || '';
        }
    };

    const getClientDepositBreakdown = (client: any) => {
        if (!client) return { currentDepositDateStr: '', settledDateStr: '', pastServiceName: 'Previous Service' };
        const currentService = client.activeService || client.activeAssignment;
        const currentDepositDate = currentService?.start_date || client.serviceStartDate || client.created_at;
        const currentDepositDateStr = formatDateDisplay(currentDepositDate);

        const pastCycles = (client.allServiceCycles || []).filter((c: any) => !c.isCurrent);
        const latestPastCycle = pastCycles[0];
        const settledDate = latestPastCycle?.endDate || latestPastCycle?.settledDate || currentService?.start_date || latestPastCycle?.startDate || client.created_at;
        const settledDateStr = formatDateDisplay(settledDate);

        return {
            currentDepositDateStr,
            settledDateStr,
            pastServiceName: latestPastCycle?.serviceType || 'Previous Service',
        };
    };

    const prevMonth = () => {
        const [y, m] = selectedMonth.split('-').map(Number);
        const d = new Date(y, m - 2, 1);
        setSelectedMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
    };

    const nextMonth = () => {
        const [y, m] = selectedMonth.split('-').map(Number);
        const now = new Date();
        const nowKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
        const next = new Date(y, m, 1);
        const nextKey = `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, '0')}`;
        if (nextKey <= nowKey) setSelectedMonth(nextKey);
    };

    const isClientInMonth = (client: any, ym: string) => {
        // 1. Client registered / joined in this month (UTC or local)
        if (client.created_at) {
            const utcKey = client.created_at.slice(0, 7);
            if (utcKey === ym) return true;
        }

        // 2. Client started a service in this specific month
        if (client.services && client.services.length > 0) {
            for (const s of client.services) {
                const sStart = s.start_date ? s.start_date.slice(0, 7) : '';
                if (sStart === ym) return true;
            }
        }

        return false;
    };

    // Remove from pipeline confirmation state
    const [requestDeleteClient, setRequestDeleteClient] = useState<any>(null);
    const [removeConfirmClient, setRemoveConfirmClient] = useState<any>(null);
    const [deleteConfirmClient, setDeleteConfirmClient] = useState<any>(null);
    const [viewingTrash, setViewingTrash] = useState(false);

    const handleDeleteClientClick = (client: any) => {
        if (user?.role !== 'admin') {
            setRequestDeleteClient(client);
        } else {
            setDeleteConfirmClient(client);
        }
    };

    const handleRemoveFromPipeline = async (client: any) => {
        const isArchived = client.status === 'Archived';

        if (isArchived) {
            // Add back to pipeline — move to Closed Won
            try {
                await supabase.from('crm_leads').update({ pipeline_stage: 'Closed Won' }).eq('id', client.id);
                setClients(prev => prev.map(c => c.id === client.id ? { ...c, status: 'Closed Won' } : c));
                toast.success(`${client.name} added back to pipeline as Closed Won.`);
            } catch (err: any) {
                toast.error(`Failed: ${err.message}`);
            }
            return;
        }

        // Show inline confirmation modal
        setRemoveConfirmClient(client);
    };

    const confirmRemoveFromPipeline = async () => {
        if (!removeConfirmClient) return;
        const client = removeConfirmClient;
        setRemoveConfirmClient(null);
        try {
            const { error } = await supabase.from('crm_leads').update({ pipeline_stage: 'Archived' }).eq('id', client.id);
            if (error) throw error;
            setClients(prev => prev.map(c => c.id === client.id ? { ...c, status: 'Archived' } : c));
            toast.success(`${client.name} removed from pipeline. Still visible in Client Master.`);
        } catch (err: any) {
            toast.error(`Failed: ${err.message}`);
        }
    };

    // Multiple Selection State
    const [selectedClientIds, setSelectedClientIds] = useState<Set<string>>(new Set());
    const [isBulkMessageModalOpen, setIsBulkMessageModalOpen] = useState<boolean>(false);

    // Filtered & Sorted Clients computed cleanly via useMemo
    const filteredClients = useMemo(() => {
        return clients.filter(c => {
            if (viewingTrash) {
                return c.status === 'Trash';
            }
            if (c.status === 'Trash') return false;

            if (searchQuery.trim()) {
                const rawQ = searchQuery.trim().toLowerCase();
                const strippedQ = rawQ.replace(/^(client\s*id|id)\s*[:#-]?\s*/i, '').trim();
                const cleanQ = rawQ.replace(/[^a-z0-9]/g, '');
                const cleanStrippedQ = strippedQ.replace(/[^a-z0-9]/g, '');

                const clientIdStr = (c.id || '').toLowerCase();
                const displayIdStr = (c.displayId || '').toLowerCase();
                const cleanClientId = clientIdStr.replace(/[^a-z0-9]/g, '');
                const cleanDisplayId = displayIdStr.replace(/[^a-z0-9]/g, '');

                const idMatch = clientIdStr.includes(rawQ) ||
                                displayIdStr.includes(rawQ) ||
                                (strippedQ.length > 0 && (clientIdStr.includes(strippedQ) || displayIdStr.includes(strippedQ))) ||
                                (cleanQ.length >= 2 && (cleanClientId.includes(cleanQ) || cleanDisplayId.includes(cleanQ))) ||
                                (cleanStrippedQ.length >= 2 && (cleanClientId.includes(cleanStrippedQ) || cleanDisplayId.includes(cleanStrippedQ)));

                const nameMatch = (c.name || '').toLowerCase().includes(rawQ) ||
                                  (strippedQ.length > 0 && (c.name || '').toLowerCase().includes(strippedQ));
                const phoneMatch = (c.phone || '').toLowerCase().includes(rawQ) ||
                                   (cleanQ.length >= 4 && (c.phone || '').replace(/\D/g, '').includes(cleanQ));
                const emailMatch = (c.email || '').toLowerCase().includes(rawQ);

                if (!nameMatch && !phoneMatch && !emailMatch && !idMatch) return false;
            }

            if (selectedCity !== 'all') {
                const cCity = c.city || extractCity(c);
                if (cCity.toLowerCase() !== selectedCity.toLowerCase()) return false;
            }

            // When actively searching, search across all months so clients from any month are found
            if (viewAllMonths || searchQuery.trim()) return true;

            return isClientInMonth(c, selectedMonth);
        });
    }, [clients, viewingTrash, searchQuery, selectedCity, viewAllMonths, selectedMonth]);

    const sortedFilteredClients = useMemo(() => {
        return [...filteredClients].sort((a, b) => {
            let comparison = 0;
            if (sortField === 'displayId') {
                comparison = (a.displayId || '').localeCompare(b.displayId || '');
            } else if (sortField === 'name') {
                comparison = (a.name || '').localeCompare(b.name || '');
            } else if (sortField === 'city') {
                const cityA = a.city || extractCity(a);
                const cityB = b.city || extractCity(b);
                comparison = cityA.localeCompare(cityB);
            } else if (sortField === 'phone') {
                comparison = (a.phone || '').localeCompare(b.phone || '');
            } else if (sortField === 'workerCount') {
                comparison = (a.activeWorkerCount || 0) - (b.activeWorkerCount || 0) || (a.workerCount || 0) - (b.workerCount || 0);
            } else if (sortField === 'securityDeposit') {
                comparison = (a.securityDeposit || 0) - (b.securityDeposit || 0);
            } else if (sortField === 'status') {
                comparison = (a.status || '').localeCompare(b.status || '');
            } else if (sortField === 'created_at') {
                const timeA = new Date(a.serviceStartDate || a.created_at || 0).getTime();
                const timeB = new Date(b.serviceStartDate || b.created_at || 0).getTime();
                comparison = timeA - timeB;
            }
            return sortAsc ? comparison : -comparison;
        });
    }, [filteredClients, sortField, sortAsc]);

    // Selection helpers
    const allVisibleSelected = sortedFilteredClients.length > 0 && sortedFilteredClients.every(c => selectedClientIds.has(c.id));
    const someVisibleSelected = sortedFilteredClients.some(c => selectedClientIds.has(c.id));

    const handleToggleSelect = (clientId: string, e?: React.MouseEvent) => {
        e?.stopPropagation();
        setSelectedClientIds(prev => {
            const next = new Set(prev);
            if (next.has(clientId)) {
                next.delete(clientId);
            } else {
                next.add(clientId);
            }
            return next;
        });
    };

    const handleToggleSelectAll = (visibleList: any[]) => {
        const visibleIds = visibleList.map(c => c.id);
        const areAllSelected = visibleIds.length > 0 && visibleIds.every(id => selectedClientIds.has(id));
        setSelectedClientIds(prev => {
            const next = new Set(prev);
            if (areAllSelected) {
                visibleIds.forEach(id => next.delete(id));
            } else {
                visibleIds.forEach(id => next.add(id));
            }
            return next;
        });
    };

    const handleClearSelection = () => {
        setSelectedClientIds(new Set());
    };

    // CSV / Excel Export with UTF-8 BOM & Excel-safe formatting
    const handleExportCsv = (clientsToExport?: any[]) => {
        const list = clientsToExport && clientsToExport.length > 0
            ? clientsToExport
            : (selectedClientIds.size > 0
                ? clients.filter(c => selectedClientIds.has(c.id))
                : sortedFilteredClients);

        if (!list || list.length === 0) {
            toast.error('No clients available to export.');
            return;
        }

        const headers = [
            'Client ID',
            'Client Name',
            'Phone Number',
            'Email',
            'City',
            'Client Status',
            'Active Service',
            'Shift / Hours',
            'Assigned Caregiver(s)',
            'Active Workers Count',
            'Total Workers Count',
            'Total Service Cycles',
            'Current Cycle Start Date',
            'Current Cycle End Date',
            'Last Cycle Settled Date',
            'Full Service History',
            'Daily Rate (Full Month)',
            'Daily Rate (Partial Month)',
            'Current Security Deposit (INR)',
            'Deposit Status',
            'Refunded/Settled Deposit (INR)',
            'Total Lifetime Deposits (INR)',
            'Lead Source',
            'Registration Date',
            'Client UUID'
        ];

        const rows = list.map(c => {
            const rawPhone = (c.phone || '').trim();
            const exportedPhone = canViewPhoneNumbers ? rawPhone : (rawPhone ? maskPhoneNumber(rawPhone, false) : '');
            // Prefix with \t inside quotes so Excel displays phone numbers as text without scientific notation or losing digits
            const excelSafePhone = exportedPhone ? `\t${exportedPhone}` : '';
            const city = c.city || extractCity(c);
            const depositAmt = (c.activeService || c.activeAssignment) ? Number(c.securityDeposit || 0) : 0;
            const pastDepositAmt = Number(c.pastDepositTotal || 0);
            const totalLifetimeDeposit = depositAmt + pastDepositAmt;

            const startDate = c.serviceStartDate ? formatDateDisplay(c.serviceStartDate) : 'N/A';
            const addedDate = c.created_at ? formatDateDisplay(c.created_at) : 'N/A';

            const currentCycle = c.allServiceCycles?.find((cyc: any) => cyc.isCurrent);
            const latestCycle = c.allServiceCycles?.[0];
            const rawSvc = c.activeService?.service_type || (c.services && c.services[0]?.service_type);
            const cleanSvc = (rawSvc && rawSvc !== 'date_range' && rawSvc !== 'open_ended' && rawSvc !== 'one_day' && rawSvc.trim() !== '') ? rawSvc : null;
            const activeServiceName = currentCycle?.serviceType || cleanSvc || latestCycle?.serviceType || ((c.services && c.services.length > 0) || c.activeAssignment ? 'Home Care Service' : 'None');

            // Shift / Hours
            const rawHours = c.hoursPerDay || c.activeService?.hours_per_day || c.activeAssignment?.hours_per_day || 10;
            const shiftLabel = rawHours >= 20 ? '24 hrs (Live-in)' : `${rawHours} hrs/day`;

            // Assigned Caregivers
            const caregiversList = c.assignedCaregivers && c.assignedCaregivers.length > 0
                ? c.assignedCaregivers.join(', ')
                : (c.activeWorkerCount > 0 ? 'Assigned' : 'Unassigned');

            // Service Cycles Tracking
            const cyclesCount = c.allServiceCycles?.length || (c.activeService ? 1 : 0);
            const totalCyclesStr = `${cyclesCount} Cycle${cyclesCount !== 1 ? 's' : ''}`;
            const currentEndDate = currentCycle?.endDate ? formatDateDisplay(currentCycle.endDate) : (c.activeService ? 'Ongoing' : 'N/A');

            // Settled Date from past cycles
            const { settledDateStr } = getClientDepositBreakdown(c);
            const lastSettledDate = settledDateStr || 'N/A';

            // Full Service History audit trail
            let fullServiceHistory = 'No service history recorded';
            if (c.allServiceCycles && c.allServiceCycles.length > 0) {
                fullServiceHistory = c.allServiceCycles.map((cyc: any, cIdx: number) => {
                    const cycleNum = c.allServiceCycles.length - cIdx;
                    const sDate = cyc.startDate ? formatDateDisplay(cyc.startDate) : 'Unknown';
                    const eDate = cyc.isCurrent ? 'Ongoing' : (cyc.endDate ? formatDateDisplay(cyc.endDate) : (cyc.settledDate ? formatDateDisplay(cyc.settledDate) : 'Ended'));
                    const depInfo = cyc.depositAmount ? `₹${cyc.depositAmount.toLocaleString('en-IN')} [${cyc.depositStatus || 'settled'}]` : 'No Deposit';
                    return `Cycle ${cycleNum}: ${cyc.serviceType || 'Service'} (${sDate} to ${eDate}, Deposit: ${depInfo})`;
                }).join('; ');
            }

            // Daily Billing Rates
            const dailyRateFull = c.completeMonthDailyRate ? `₹${Number(c.completeMonthDailyRate).toLocaleString('en-IN')}/day` : 'N/A';
            const dailyRatePartial = c.incompleteMonthDailyRate ? `₹${Number(c.incompleteMonthDailyRate).toLocaleString('en-IN')}/day` : 'N/A';

            // Deposit Status Formatted
            const formattedDepositStatus = c.depositStatus ? (c.depositStatus.charAt(0).toUpperCase() + c.depositStatus.slice(1)) : 'Settled';

            return [
                c.displayId || 'CL-0001',
                c.name || '',
                excelSafePhone,
                c.email && c.email !== '-' ? c.email : '',
                city,
                c.status || '',
                activeServiceName,
                shiftLabel,
                caregiversList,
                c.activeWorkerCount || 0,
                c.workerCount || 0,
                totalCyclesStr,
                startDate,
                currentEndDate,
                lastSettledDate,
                fullServiceHistory,
                dailyRateFull,
                dailyRatePartial,
                depositAmt,
                formattedDepositStatus,
                pastDepositAmt,
                totalLifetimeDeposit,
                c.leadSource || 'Manual Add',
                addedDate,
                c.id || ''
            ];
        });

        // Prepend UTF-8 BOM (\uFEFF) so Excel respects UTF-8 encoding
        const csvContent = '\uFEFF' + [
            headers.map(h => `"${String(h).replace(/"/g, '""')}"`).join(','),
            ...rows.map(r => r.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
        ].join('\r\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.setAttribute('href', url);
        const prefix = selectedClientIds.size > 0 && clientsToExport
            ? `Selected_${list.length}_Clients_`
            : `Clients_${selectedCity !== 'all' ? selectedCity + '_' : ''}${!viewAllMonths ? selectedMonth + '_' : 'All_'}`;
        link.setAttribute('download', `99Care_${prefix}${new Date().toISOString().slice(0, 10)}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        toast.success(`Exported ${list.length} client${list.length !== 1 ? 's' : ''} to CSV/Excel successfully!`);

        // Record audit trail of exported client data
        logClientExport(
            'csv',
            list.length,
            list.slice(0, 5).map((c: any) => c.name || c.client_name),
            {
                selectedCity,
                selectedMonth: !viewAllMonths ? selectedMonth : 'all',
                isSelection: selectedClientIds.size > 0 && !!clientsToExport,
            },
            user ? { id: user.id, name: user.name, role: user.role } : undefined
        );
    };

    const confirmTemporaryDelete = async () => {
        if (!deleteConfirmClient) return;
        if (user?.role !== 'admin') {
            toast.error('Only System Admin can delete records.');
            setDeleteConfirmClient(null);
            return;
        }
        const client = deleteConfirmClient;
        setDeleteConfirmClient(null);
        try {
            const { error } = await supabase.from('crm_leads').update({ pipeline_stage: 'Trash' }).eq('id', client.id);
            if (error) throw error;
            setClients(prev => prev.map(c => c.id === client.id ? { ...c, status: 'Trash' } : c));
            toast.success(`${client.name} moved to trash.`);
        } catch (err: any) {
            toast.error(`Failed to move to trash: ${err.message}`);
        }
    };
    
    const handleRestoreClient = async (client: any) => {
        try {
            const { error } = await supabase.from('crm_leads').update({ pipeline_stage: 'Archived' }).eq('id', client.id);
            if (error) throw error;
            setClients(prev => prev.map(c => c.id === client.id ? { ...c, status: 'Archived' } : c));
            toast.success(`${client.name} restored from trash.`);
        } catch (err: any) {
            toast.error(`Failed to restore: ${err.message}`);
        }
    };

    const confirmDeleteClient = async () => {
        if (!deleteConfirmClient) return;
        if (user?.role !== 'admin') {
            toast.error('Only System Admin can delete records.');
            setDeleteConfirmClient(null);
            return;
        }
        const client = deleteConfirmClient;
        setDeleteConfirmClient(null);
        try {
            // First attempt to delete from crm_leads in case it's a foreign key parent
            await supabase.from('crm_leads').delete().eq('id', client.id);
            // Then delete from clients table
            const { error } = await supabase.from('clients').delete().eq('id', client.id);
            if (error) throw error;
            
            setClients(prev => prev.filter(c => c.id !== client.id));
            toast.success(`${client.name} permanently deleted from master database.`);
        } catch (err: any) {
            toast.error(`Failed to delete: ${err.message}`);
        }
    };

    const openRestartModal = async (client: any) => {
        setRestartModal(client);

        // Extract previous service configuration if available
        const services = client.services || [];
        const lastService = services.length > 0 ? services[services.length - 1] : null;

        let leadInterest = '';
        let leadQuotedRate = 0;
        let leadFull: any = null;
        try {
            const { data: leadData } = await supabase
                .from('crm_leads')
                .select('*, client_consents(*)')
                .eq('id', client.id)
                .maybeSingle();
            if (leadData) {
                leadFull = leadData;
                leadInterest = leadData.service_interest || '';
                leadQuotedRate = leadData.quoted_monthly_rate || 0;
            }
        } catch (e) {
            console.warn('Could not fetch lead details for restart:', e);
        }

        const rawSvcType = lastService?.service_type;
        const validSvcType = (rawSvcType && rawSvcType !== 'date_range' && rawSvcType !== 'open_ended' && rawSvcType !== 'one_day' && rawSvcType.trim() !== '') ? rawSvcType : null;
        const initialService = validSvcType || leadInterest || 'Old Age Care';
        const rawHours = lastService?.hours_per_day || 10;
        const initialHours = rawHours >= 20 ? 24 : 10;
        const initialComplete = lastService?.complete_month_daily_rate || (leadQuotedRate ? Math.round(leadQuotedRate / 30) : 800);
        const initialIncomplete = lastService?.incomplete_month_daily_rate || 1500;
        const todayStr = new Date().toISOString().split('T')[0];

        // Parse last service notes JSON if available
        let lastNotesParsed: any = null;
        if (lastService?.notes) {
            try {
                const trimmed = String(lastService.notes).trim();
                if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
                    lastNotesParsed = JSON.parse(trimmed);
                }
            } catch {}
        }

        const consents = (leadFull?.client_consents || []).sort(
            (a: any, b: any) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime()
        );
        const latestConsent = consents[0] || null;
        const workFormData = leadFull?.work_form_data || null;

        const sType = initialService.toLowerCase();
        const isBaby = /baby|maternity|jhapa|japa|infant|child/.test(sType);
        const initialFormType: 'baby_care_form' | 'patient_care_form' = 
            lastNotesParsed?.form_type || workFormData?.form_type || (isBaby ? 'baby_care_form' : 'patient_care_form');

        // Extract patient care fields with fallbacks
        const pName = lastNotesParsed?.patient_name || latestConsent?.patient_name || workFormData?.patient_name || client.name || '';
        const rawAge = lastNotesParsed?.age || (latestConsent?.age != null ? String(latestConsent.age) : '') || workFormData?.age || '';
        const rawAgeUnit = lastNotesParsed?.age_unit || latestConsent?.age_unit || workFormData?.age_unit || (isBaby ? 'Days' : 'Years');
        const rawWeight = lastNotesParsed?.weight || (latestConsent?.weight != null ? String(latestConsent.weight) : '') || workFormData?.weight || '';

        const parsed = parseAgeAndWeight(rawAge, rawAgeUnit, rawWeight, isBaby);
        const pAge = parsed.age;
        const pAgeUnit = parsed.ageUnit;
        const pWeight = parsed.weight;

        const pRelative = lastNotesParsed?.relative_name || latestConsent?.relative_name || workFormData?.relative_name || client.name || '';
        const pContact = lastNotesParsed?.contact_number || latestConsent?.contact_number || workFormData?.contact_number || client.phone || '';
        const pAltContact = lastNotesParsed?.alternate_contact_number || latestConsent?.alternate_contact_number || workFormData?.alternate_contact_number || '';
        const pAddress = lastNotesParsed?.address || latestConsent?.address || workFormData?.address || client.address || leadFull?.location || '';
        const pMedical = lastNotesParsed?.medical_condition || latestConsent?.other_details || workFormData?.medical_condition || '';

        // Extract duties
        let initialDuties: string[] = [];
        if (Array.isArray(lastNotesParsed?.duties) && lastNotesParsed.duties.length > 0) {
            initialDuties = lastNotesParsed.duties;
        } else if (Array.isArray(workFormData?.duties) && workFormData.duties.length > 0) {
            initialDuties = workFormData.duties;
        }

        const initialCareInstructions = lastNotesParsed?.instructions || lastNotesParsed?.other_work || workFormData?.other_work || '';

        setRestartServiceType(initialService);
        setRestartShiftHours(initialHours);
        setRestartStartDate(todayStr);
        setRestartEndDate('');
        setRestartIsOngoing(true);
        setRestartCompleteRate(initialComplete);
        setRestartIncompleteRate(initialIncomplete);
        setRestartDepositAmount(5000);
        setRestartDepositStatus('collected');
        setRestartDepositMethod('UPI');
        setRestartWorkerSearch('');
        setRestartSelectedWorkers([]);
        setRestartNotes('');

        setRestartLeadFull(leadFull);
        setRestartPatientName(pName);
        setRestartAge(pAge);
        setRestartAgeUnit(pAgeUnit);
        setRestartWeight(pWeight);
        setRestartRelativeName(pRelative);
        setRestartContactNumber(pContact);
        setRestartAltContactNumber(pAltContact);
        setRestartAddress(pAddress);
        setRestartMedicalCondition(pMedical);
        setRestartFormType(initialFormType);
        setRestartDuties(initialDuties);
        setRestartCareInstructions(initialCareInstructions);
        setIsManualCareExpanded(false);

        // Fetch workers with their stored 10hr and 24hr daily payout rates
        const { data } = await supabase
            .from('employees')
            .select('id, full_name, job_title, status, photo_url, rate_10hr, rate_24hr, assigned_client')
            .order('full_name');
        setRestartWorkers(data || []);
    };

    const handleSelectRestartServiceCategory = (cat: string) => {
        setRestartServiceType(cat);
        const isBaby = /baby|maternity|jhapa|japa|infant|child/.test(cat.toLowerCase());
        const newFormType: 'baby_care_form' | 'patient_care_form' = isBaby ? 'baby_care_form' : 'patient_care_form';
        if (newFormType !== restartFormType) {
            setRestartFormType(newFormType);
            const targetDict = newFormType === 'baby_care_form' ? BABY_CARE_DUTY_LABELS : PATIENT_CARE_DUTY_LABELS;
            const valid = restartDuties.filter(d => d in targetDict);
            setRestartDuties(valid);
            if (newFormType === 'baby_care_form' && restartAgeUnit === 'Years') {
                setRestartAgeUnit('Months');
            } else if (newFormType === 'patient_care_form' && (restartAgeUnit === 'Months' || restartAgeUnit === 'Days')) {
                setRestartAgeUnit('Years');
            }
        }
    };

    const handleAgeInputChange = (val: string) => {
        if (/(?:kgs?|kg|kilos?|years?|months?|days?)/i.test(val)) {
            const parsed = parseAgeAndWeight(val, restartAgeUnit, restartWeight, restartFormType === 'baby_care_form');
            setRestartAge(parsed.age);
            if (parsed.weight && !restartWeight) {
                setRestartWeight(parsed.weight);
            }
            setRestartAgeUnit(parsed.ageUnit);
        } else {
            setRestartAge(val);
        }
    };

    const handleWeightInputChange = (val: string) => {
        const clean = val.replace(/\s*(?:kgs?|kg|kilos?)\b/gi, '').trim();
        setRestartWeight(clean);
    };

    const handleSaveCareDetails = async () => {
        if (!restartModal?.id) {
            toast.error('Client record not found.');
            return;
        }

        setIsSavingCareDetails(true);
        const toastId = toast.loading('Saving care details & duties...');
        try {
            const workFormData = {
                form_type: restartFormType,
                patient_name: restartPatientName.trim() || restartModal.name,
                age: restartAge.trim(),
                age_unit: restartAgeUnit,
                weight: restartWeight.trim(),
                relative_name: restartRelativeName.trim() || restartModal.name,
                contact_number: restartContactNumber.trim() || (restartModal.phone || '').replace(/\D/g, ''),
                alternate_contact_number: restartAltContactNumber.trim(),
                address: restartAddress.trim(),
                medical_condition: restartMedicalCondition.trim(),
                duties: restartDuties,
                other_work: restartCareInstructions.trim(),
                updated_at: new Date().toISOString()
            };

            const leadUpdates: any = {
                work_form_data: workFormData,
                updated_at: new Date().toISOString()
            };
            if (restartAddress.trim()) {
                leadUpdates.location = restartAddress.trim();
            }

            // 1. Update crm_leads
            const { error: leadErr } = await supabase
                .from('crm_leads')
                .update(leadUpdates)
                .eq('id', restartModal.id);

            if (leadErr) console.warn('Could not update crm_leads work_form_data:', leadErr);

            // 2. Insert into client_consents snapshot
            const consentPayload = {
                lead_id: restartModal.id,
                patient_name: restartPatientName.trim() || restartModal.name,
                age: restartAge.trim() || null,
                age_unit: restartAgeUnit,
                weight: restartWeight.trim() || null,
                relative_name: restartRelativeName.trim() || restartModal.name,
                contact_number: restartContactNumber.trim() || restartModal.phone || null,
                alternate_contact_number: restartAltContactNumber.trim() || null,
                address: restartAddress.trim() || null,
                other_details: restartMedicalCondition.trim() || null,
                service_type: restartServiceType || undefined,
                created_at: new Date().toISOString()
            };
            const { error: consentErr } = await supabase.from('client_consents').insert([consentPayload]);
            if (consentErr) console.warn('Could not insert client_consents:', consentErr);

            // 3. Mirror into client_work_forms
            const { error: wfErr } = await supabase.from('client_work_forms').insert([{
                lead_id: restartModal.id,
                form_type: restartFormType,
                patient_name: restartPatientName.trim() || restartModal.name,
                duties: restartDuties,
                other_work: restartCareInstructions.trim()
            }]);
            if (wfErr) console.warn('Could not mirror client_work_forms:', wfErr);

            // 4. Update in-memory restartLeadFull so preview and subsequent actions stay synchronized
            setRestartLeadFull((prev: any) => ({
                ...(prev || {}),
                work_form_data: workFormData,
                location: restartAddress.trim() || prev?.location
            }));

            toast.success('Patient care details and caregiver duties saved successfully! ✨', { id: toastId });
            setIsManualCareExpanded(false);
        } catch (err: any) {
            console.error('Error saving care details:', err);
            toast.error(`Failed to save care details: ${err.message || 'Unknown error'}`, { id: toastId });
        } finally {
            setIsSavingCareDetails(false);
        }
    };

    const handleSendConsentWhatsApp = async () => {
        if (!restartModal) return;
        const rawPhone = (restartContactNumber || restartModal.phone || '').replace(/\D/g, '');
        const cleanPhone = rawPhone.startsWith('91') ? rawPhone : `91${rawPhone}`;
        if (!cleanPhone || cleanPhone.length < 10) {
            return toast.error('No valid phone number found for this client.');
        }

        setIsSendingConsent(true);
        const toastId = toast.loading('Sending Consent Form via WhatsApp...');
        try {
            const firstName = (restartModal.name || '').split(' ')[0] || 'there';
            const leadPayload = restartLeadFull || {
                id: restartModal.id,
                name: restartModal.name,
                phone: cleanPhone,
                whatsapp_number: cleanPhone,
                client_consents: [{
                    patient_name: restartPatientName || restartModal.name,
                    age: restartAge,
                    age_unit: restartAgeUnit,
                    weight: restartWeight,
                    relative_name: restartRelativeName || restartModal.name,
                    contact_number: restartContactNumber || cleanPhone,
                    alternate_contact_number: restartAltContactNumber,
                    address: restartAddress,
                    other_details: restartMedicalCondition,
                    service_category: restartServiceType,
                    service_start_date: restartStartDate,
                    offered_time: restartShiftHours === 24 ? '24 Hours (Live-in)' : '10 Hours',
                }]
            };

            const flowData = buildConsentFlowActionData(leadPayload, {
                extra: {
                    patient_name: restartPatientName || '',
                    age: restartAge || '',
                    weight: restartWeight || '',
                    relative_name: restartRelativeName || restartModal.name || '',
                    contact_number: restartContactNumber || cleanPhone,
                    alternate_contact_number: restartAltContactNumber || '',
                    address: restartAddress || '',
                    service_category: restartServiceType || '',
                    service_start_date: restartStartDate || '',
                    other_details: restartMedicalCondition || '',
                }
            });

            const { data, error } = await supabase.functions.invoke('meta-whatsapp-outbound', {
                body: {
                    phone: cleanPhone,
                    leadId: restartModal.id,
                    leadName: firstName,
                    leadFullName: restartModal.name,
                    useTemplate: true,
                    templateName: 'consent_form',
                    flowData
                }
            });

            if (error || data?.success === false) {
                console.warn('Direct Meta API dispatch failed, opening WhatsApp fallback:', error || data?.error);
                const fallbackText = `Namaste ${restartModal.name} ji! 🙏\n\nHum aapki nayi *${restartServiceType}* service (${restartStartDate} se) ke liye patient care aur consent details confirm karna chahte hain:\n\n👤 *Patient Name*: ${restartPatientName || 'N/A'}\n🎂 *Age / Weight*: ${restartAge ? `${restartAge} ${restartAgeUnit}` : 'N/A'}${restartWeight ? ` • ${restartWeight} kg` : ''}\n👨‍👩‍👧 *Guardian*: ${restartRelativeName || restartModal.name}\n📍 *Service Address*: ${restartAddress || 'N/A'}\n🏥 *Condition / Notes*: ${restartMedicalCondition || 'None'}\n\nKripya confirm karein ya koi badlav ho toh batayein.\n\nDhanyawad,\n99 Care Team`;
                window.open(`https://wa.me/${cleanPhone}?text=${encodeURIComponent(fallbackText)}`, '_blank');
                toast.info('Opened WhatsApp chat with consent details for client verification.', { id: toastId });
            } else {
                toast.success('✅ Consent form dispatched to client WhatsApp!', { id: toastId });
            }
        } catch (err: any) {
            console.error('Consent dispatch error:', err);
            toast.error(`Dispatch failed: ${err.message || 'Check connection'}`, { id: toastId });
        } finally {
            setIsSendingConsent(false);
        }
    };

    const handleSendWorkFormWhatsApp = async () => {
        if (!restartModal) return;
        const rawPhone = (restartContactNumber || restartModal.phone || '').replace(/\D/g, '');
        const cleanPhone = rawPhone.startsWith('91') ? rawPhone : `91${rawPhone}`;
        if (!cleanPhone || cleanPhone.length < 10) {
            return toast.error('No valid phone number found for this client.');
        }

        setIsSendingWorkForm(true);
        const toastId = toast.loading('Sending Caregiver Work Form via WhatsApp...');
        try {
            const firstName = (restartModal.name || '').split(' ')[0] || 'there';
            const isBaby = restartFormType === 'baby_care_form';
            const templateName = isBaby ? 'baby_care_form' : 'patient_care_form';

            const leadPayload = restartLeadFull || {
                id: restartModal.id,
                name: restartModal.name,
                phone: cleanPhone,
                whatsapp_number: cleanPhone,
            };

            const flowData = isBaby
                ? buildBabyCareFlowActionData(leadPayload, {
                    extra: {
                        baby_name: restartPatientName || '',
                        date: restartStartDate || '',
                    }
                })
                : buildPatientCareFlowActionData(leadPayload, {
                    extra: {
                        full_name: restartPatientName || restartModal.name || '',
                    }
                });

            const { data, error } = await supabase.functions.invoke('meta-whatsapp-outbound', {
                body: {
                    phone: cleanPhone,
                    leadId: restartModal.id,
                    leadName: firstName,
                    useTemplate: true,
                    templateName,
                    flowData,
                }
            });

            if (error || data?.success === false) {
                console.warn('Direct Meta API dispatch failed, opening WhatsApp fallback:', error || data?.error);
                const dict = isBaby ? BABY_CARE_DUTY_LABELS : PATIENT_CARE_DUTY_LABELS;
                const dutyNames = restartDuties.length > 0 
                    ? restartDuties.map(d => dict[d] || d).join('\n• ') 
                    : 'All standard caregiving duties';
                const fallbackText = `Namaste ${restartModal.name} ji! 🙏\n\nHum aapki ${restartServiceType} care service ke liye caregiver duties checklist confirm karna chahte hain:\n\n*Form Type*: ${isBaby ? 'Newborn Baby Care' : 'Patient Care'}\n*Patient / Baby*: ${restartPatientName || restartModal.name}\n\n*Selected Care Duties:*\n• ${dutyNames}\n${restartCareInstructions ? `\n*Special Instructions:*\n${restartCareInstructions}\n` : ''}\nKripya batayein agar koi aur vishesh kaam shamil karna ho.\n\nDhanyawad,\n99 Care Support`;
                window.open(`https://wa.me/${cleanPhone}?text=${encodeURIComponent(fallbackText)}`, '_blank');
                toast.info('Opened WhatsApp chat with caregiver duties for client verification.', { id: toastId });
            } else {
                toast.success(`✅ ${isBaby ? 'Baby Care' : 'Patient Care'} work form dispatched to client!`, { id: toastId });
            }
        } catch (err: any) {
            console.error('Work form dispatch error:', err);
            toast.error(`Dispatch failed: ${err.message || 'Check connection'}`, { id: toastId });
        } finally {
            setIsSendingWorkForm(false);
        }
    };

    const handleRestartService = async () => {
        if (!restartStartDate) return toast.error('Please select a service start date.');
        if (!restartServiceType.trim()) return toast.error('Please specify the service name.');
        if (!restartCompleteRate || Number(restartCompleteRate) <= 0) return toast.error('Please enter a valid complete month daily rate.');
        if (!restartIncompleteRate || Number(restartIncompleteRate) <= 0) return toast.error('Please enter a valid incomplete month daily rate.');
        if (restartSelectedWorkers.length === 0) return toast.error('Please select at least one staff member to assign.');

        setIsRestartSubmitting(true);
        try {
            // Automatically determine worker payout according to their stored shift rate
            const workersPayload = restartSelectedWorkers.map(w => ({
                workerId: w.id,
                workerPayoutRate: restartShiftHours === 24
                    ? (w.rate_24hr ? Number(w.rate_24hr) : undefined)
                    : (w.rate_10hr ? Number(w.rate_10hr) : undefined)
            }));

            const result = await restartClientService({
                clientId: restartModal.id,
                clientName: restartModal.name,
                serviceType: restartServiceType.trim(),
                hoursPerDay: Number(restartShiftHours) === 24 ? 24 : 10,
                startDate: restartStartDate,
                endDate: restartIsOngoing ? null : (restartEndDate || null),
                completeMonthDailyRate: Number(restartCompleteRate),
                incompleteMonthDailyRate: Number(restartIncompleteRate),
                depositAmount: Number(restartDepositAmount) || 0,
                depositStatus: restartDepositStatus,
                depositPaymentMethod: restartDepositMethod,
                workers: workersPayload,
                notes: restartNotes.trim() || undefined,
                careScope: {
                    patientName: restartPatientName.trim() || restartModal.name,
                    age: restartAge.trim(),
                    ageUnit: restartAgeUnit,
                    weight: restartWeight.trim(),
                    relativeName: restartRelativeName.trim() || restartModal.name,
                    contactNumber: restartContactNumber.trim() || (restartModal.phone || '').replace(/\D/g, ''),
                    alternateContactNumber: restartAltContactNumber.trim(),
                    address: restartAddress.trim(),
                    medicalCondition: restartMedicalCondition.trim(),
                    formType: restartFormType,
                    duties: restartDuties,
                    instructions: restartCareInstructions.trim() || restartNotes.trim(),
                }
            });

            if (!result.success) throw new Error(result.error);

            toast.success(`Service restarted for ${restartModal.name}! 🎉 Moved back to Active Client.`);

            // Record audit trail of service modification
            logAuditEvent({
                entityType: 'client',
                entityId: restartModal.id,
                entityName: restartModal.name,
                action: 'SERVICE_CHANGE',
                description: `Restarted service cycle: ${restartServiceType.trim()} (${restartShiftHours}hr shift) from ${restartStartDate}`,
                userId: user?.id,
                userName: user?.name,
                userRole: user?.role,
                changes: {
                    fields: ['service_type', 'hours_per_day', 'start_date', 'complete_month_daily_rate', 'workers'],
                    after: {
                        serviceType: restartServiceType.trim(),
                        hoursPerDay: restartShiftHours,
                        startDate: restartStartDate,
                        rate: restartCompleteRate,
                        workersCount: restartSelectedWorkers.length,
                    }
                }
            });

            setRestartModal(null);
            fetchClients();
        } catch (err: any) {
            console.error('Failed to restart service:', err);
            toast.error(`Failed to restart service: ${err.message || 'Unknown error'}`);
        } finally {
            setIsRestartSubmitting(false);
        }
    };


    const handleRequestReview = async (client: any) => {
        if (!client.phone) {
            toast.error("No phone number found for this client. Please update the profile.");
            return;
        }

        const toastId = toast.loading(`Sending WhatsApp Review request to ${client.name}...`);
        
        try {
            // Standardize phone
            let phoneDigits = client.phone.replace(/\D/g, '');
            if (phoneDigits.length === 10) phoneDigits = `91${phoneDigits}`;

            const firstName = client.name.split(' ')[0] || 'there';

            const { data, error } = await supabase.functions.invoke('meta-whatsapp-outbound', {
                body: {
                    phone: phoneDigits,
                    useTemplate: true,
                    templateName: 'client_review_request',
                    templateParams: [firstName]
                }
            });

            if (error) throw error;
            if (data?.success === false) throw new Error(data.error || "Meta API error");

            toast.success('Review request sent successfully! ✅', { id: toastId });
            setReviewSentIds(prev => new Set(prev).add(client.id));
        } catch (err: any) {
            console.error('WhatsApp Review fail:', err);
            toast.error(`WhatsApp failed: ${err.message || 'Check connection'}`, { id: toastId });
        }
    };


    const fetchClients = async () => {
        try {
            // 1. Fetch leads to map pipeline_stage and service metadata
            // Also fetch all records from the clients table
            const [leadsResult, allClientsRes] = await Promise.all([
                supabase.from('crm_leads').select('id, name, phone, whatsapp_number, email, source, created_at, pipeline_stage, notes, assigned_worker_role, assigned_worker_name, complete_month_daily_rate, incomplete_month_daily_rate'),
                supabase.from('clients').select('*').order('created_at', { ascending: false })
            ]);

            if (leadsResult.error) throw leadsResult.error;
            if (allClientsRes.error) throw allClientsRes.error;

            const allLeads = leadsResult.data || [];
            // Build a map of lead_id -> pipeline_stage and lead_id -> lead data for status badge and service metadata
            const stageMap: Record<string, string> = {};
            const leadMap: Record<string, any> = {};
            allLeads.forEach(l => {
                stageMap[l.id] = l.pipeline_stage || 'Archived';
                leadMap[l.id] = l;
            });

            const clientData = [...(allClientsRes.data || [])];
            const existingClientIds = new Set(clientData.map(c => c.id));

            // Self-heal: If any confirmed active lead in CRM is missing from clients table, include and persist it
            const activeLeads = allLeads.filter(l => ['Active Client', 'Monthly Billing', 'Closed Won'].includes(l.pipeline_stage));
            const missingLeads = activeLeads.filter(l => !existingClientIds.has(l.id));
            if (missingLeads.length > 0) {
                missingLeads.forEach(l => {
                    const fallbackClient = {
                        id: l.id,
                        client_name: l.name,
                        company_name: null,
                        phone_number: l.phone || l.whatsapp_number || null,
                        email: l.email || null,
                        source: l.source || null,
                        created_at: l.created_at,
                    };
                    clientData.push(fallbackClient);
                    supabase.from('clients').upsert({
                        id: l.id,
                        client_name: l.name,
                        phone_number: l.phone || l.whatsapp_number || null,
                        email: l.email || null,
                        source: l.source || null,
                        created_at: l.created_at,
                    }, { onConflict: 'id' }).then();
                });
            }

            // 3. Fetch worker_assignments with employee status & dates
            const { data: allAssignmentsData, error: assignAllError } = await supabase
                .from('worker_assignments')
                .select('id, client_id, assignment_status, employee_id, deposit_amount, deposit_paid, start_date, end_date, service_type, hours_per_day, assigned_at, notes, employees(status, full_name)');

            if (assignAllError) throw assignAllError;

            // 4. Fetch payment data for deposit fallback & all services with deposit info
            const [{ data: depositPaymentsData, error: paymentsError }, { data: allServicesData }] = await Promise.all([
                supabase
                    .from('payments')
                    .select('client_name, amount, payment_type')
                    .eq('payment_type', 'deposit'),
                supabase
                    .from('services')
                    .select('id, client_id, service_type, hours_per_day, complete_month_daily_rate, incomplete_month_daily_rate, start_date, end_date, status, deposit_amount, deposit_status, created_at, notes, legacy_assignment_id, service_worker_assignments(id, employee_id, start_date, end_date, employees(status, full_name))')
            ]);

            if (paymentsError) throw paymentsError;

            const assignments = allAssignmentsData || [];
            const depositPayments = depositPaymentsData || [];
            const services = allServicesData || [];

            // Build unified workerMap keyed by client_id combining BOTH worker_assignments AND service_worker_assignments
            const allWorkersByClient: Record<string, Set<string>> = {};
            const activeWorkersByClient: Record<string, Set<string>> = {};
            const activeWorkerNamesByClient: Record<string, Set<string>> = {};

            const ensureSets = (clientId: string) => {
                if (!allWorkersByClient[clientId]) allWorkersByClient[clientId] = new Set();
                if (!activeWorkersByClient[clientId]) activeWorkersByClient[clientId] = new Set();
                if (!activeWorkerNamesByClient[clientId]) activeWorkerNamesByClient[clientId] = new Set();
            };

            const todayStr = new Date().toISOString().split('T')[0];

            // 1. Process worker_assignments
            assignments.forEach((a: any) => {
                if (!a.client_id) return;
                ensureSets(a.client_id);
                if (a.employee_id) allWorkersByClient[a.client_id].add(a.employee_id);
                const empStatus = a.employees?.status;
                const isActive = a.assignment_status === 'active' && (!a.end_date || a.end_date >= todayStr) && (empStatus === 'assigned' || empStatus === 'Active' || empStatus === 'available');
                if (isActive && a.employee_id) {
                    activeWorkersByClient[a.client_id].add(a.employee_id);
                    if (a.employees?.full_name) activeWorkerNamesByClient[a.client_id].add(a.employees.full_name);
                }
            });

            // 2. Process service_worker_assignments from services
            services.forEach((s: any) => {
                if (!s.client_id) return;
                ensureSets(s.client_id);
                (s.service_worker_assignments || []).forEach((swa: any) => {
                    if (swa.employee_id) allWorkersByClient[s.client_id].add(swa.employee_id);
                    const empStatus = swa.employees?.status;
                    const isActive = s.status === 'active' && (!swa.end_date || swa.end_date >= todayStr) && (empStatus === 'assigned' || empStatus === 'Active' || empStatus === 'available');
                    if (isActive && swa.employee_id) {
                        activeWorkersByClient[s.client_id].add(swa.employee_id);
                        if (swa.employees?.full_name) activeWorkerNamesByClient[s.client_id].add(swa.employees.full_name);
                    }
                });
            });

            const workerMap: Record<string, { workerCount: number, activeWorkerCount: number, activeWorkerNames: string[] }> = {};
            Object.keys(allWorkersByClient).forEach(cid => {
                const namesSet = activeWorkerNamesByClient[cid] || new Set();
                if (namesSet.size === 0 && leadMap[cid]?.assigned_worker_name) {
                    namesSet.add(leadMap[cid].assigned_worker_name);
                }
                workerMap[cid] = {
                    workerCount: allWorkersByClient[cid].size,
                    activeWorkerCount: activeWorkersByClient[cid].size,
                    activeWorkerNames: Array.from(namesSet)
                };
            });

            const normalizeClientName = (name?: string | null) => (name || '').trim().toLowerCase();

            // Map deposits by client name for legacy fallback
            const paidDepositByClientName: Record<string, number> = {};
            depositPayments.forEach(p => {
                const clientName = normalizeClientName(p.client_name);
                if (!clientName) return;
                paidDepositByClientName[clientName] = (paidDepositByClientName[clientName] || 0) + (Number(p.amount) || 0);
            });

            // 5. Map database clients to UI structure (Per-service deposit calculation - NEVER merge across cycles)
            // Qualification rule:
            // For a lead's first service, they should only appear in Client Master Database AFTER the deposit is collected.
            // If the same client starts another service after this ends, they should remain in Client Master Database.
            const qualifiedClients = (clientData || []).filter(c => {
                const clientServices = services.filter(s => s.client_id === c.id);
                const clientAssignments = assignments.filter(a => a.client_id === c.id && a.assignment_status !== 'cancelled' && !a.notes?.toLowerCase().includes('superseded'));

                // Has this client ever had a deposit collected or settled, or payment recorded?
                const hasCollectedOrSettledDeposit = clientServices.some(s => s.deposit_status === 'collected' || s.deposit_status === 'settled')
                    || clientAssignments.some(a => Number(a.deposit_paid) > 0)
                    || (paidDepositByClientName[normalizeClientName(c.client_name)] || 0) > 0;

                // Has this client completed any historical / ended service?
                const hasCompletedHistoricalService = clientServices.some(s => s.status !== 'active')
                    || clientAssignments.some(a => a.assignment_status === 'completed' || a.assignment_status === 'ended');

                const leadStage = leadMap[c.id]?.pipeline_stage;
                const isPreDepositLead = ['New Lead', 'New', 'New Inquiry', 'In Discussion', 'Quotation Sent', 'Consent Form', 'Staff Assigned', 'Deposit Pending'].includes(leadStage);

                // If this lead is on their first service and deposit is NOT collected yet, don't show in Client Master Database
                if (isPreDepositLead && !hasCollectedOrSettledDeposit && !hasCompletedHistoricalService) {
                    return false;
                }

                return true;
            });

            // Deterministic chronological ordering for human-readable Client Code (CL-0001 ... CL-XXXX)
            const sortedChronologically = [...qualifiedClients].sort((a, b) => {
                const timeA = new Date(a.created_at || 0).getTime();
                const timeB = new Date(b.created_at || 0).getTime();
                if (timeA !== timeB) return timeA - timeB;
                return (a.id || '').localeCompare(b.id || '');
            });

            const clientCodeMap = new Map<string, string>();
            sortedChronologically.forEach((c, index) => {
                clientCodeMap.set(c.id, `CL-${String(index + 1).padStart(4, '0')}`);
            });

            const enrichedClients = qualifiedClients.map(c => {
                const clientServices = services.filter(s => s.client_id === c.id);
                const clientAssignments = assignments.filter(a => a.client_id === c.id && a.assignment_status !== 'cancelled' && !a.notes?.toLowerCase().includes('superseded'));

                const activeService = clientServices.find(s => s.status === 'active');
                const activeAssignment = clientAssignments.find(a => a.assignment_status === 'active');

                const latestService = [...clientServices].sort((a, b) => new Date(b.created_at || b.start_date || 0).getTime() - new Date(a.created_at || a.start_date || 0).getTime())[0];
                const latestAssignment = [...clientAssignments].sort((a, b) => new Date(b.assigned_at || b.start_date || 0).getTime() - new Date(a.assigned_at || a.start_date || 0).getTime())[0];

                let depositAmount = 0;
                let depositStatus: 'collected' | 'pending' | 'settled' = 'settled';

                if (activeService) {
                    depositAmount = Number(activeService.deposit_amount) || 5000;
                    depositStatus = (activeService.deposit_status as 'collected' | 'pending' | 'settled') || 'pending';
                } else if (activeAssignment) {
                    depositAmount = Number(activeAssignment.deposit_amount) || 0;
                    depositStatus = (Number(activeAssignment.deposit_paid) >= depositAmount && depositAmount > 0) ? 'collected' : 'pending';
                } else {
                    depositAmount = 0;
                    depositStatus = 'settled';
                }

                // Build unified service cycles history with deposit tracking
                const leadInfo = leadMap[c.id];
                const formatServiceLabel = (type?: string, notes?: string) => {
                    if (type && type !== 'date_range' && type !== 'open_ended' && type !== 'one_day' && type.trim() !== '') {
                        return type;
                    }
                    if (leadInfo?.assigned_worker_role) return leadInfo.assigned_worker_role;
                    if (leadInfo?.notes) {
                        const match = leadInfo.notes.match(/Service:\s*([^\n\r]+)/i);
                        if (match && match[1]?.trim()) return match[1].trim();
                    }
                    if (notes) {
                        const match = notes.match(/Service:\s*([^\n\r]+)/i);
                        if (match && match[1]?.trim()) return match[1].trim();
                    }
                    return 'Home Care Service';
                };

                const allServiceCycles: any[] = [];
                if (clientServices.length > 0) {
                    const sortedServices = [...clientServices].sort((a, b) => new Date(b.created_at || b.start_date || 0).getTime() - new Date(a.created_at || a.start_date || 0).getTime());
                    sortedServices.forEach((s, sIdx) => {
                        const isCurrent = activeService ? s.id === activeService.id : false;
                        const nextSvc = sortedServices[sIdx - 1];
                        const cycleStartDate = s.start_date || s.created_at || c.created_at;
                        const cycleEndDate = isCurrent ? (s.end_date || null) : (s.end_date || (nextSvc ? (nextSvc.start_date || nextSvc.created_at) : (activeService?.start_date || null)));
                        const cycleSettledDate = isCurrent ? null : (cycleEndDate || s.end_date || activeService?.start_date || s.created_at || c.created_at);

                        allServiceCycles.push({
                            id: s.id,
                            serviceType: formatServiceLabel(s.service_type, s.notes),
                            startDate: cycleStartDate,
                            endDate: cycleEndDate,
                            settledDate: cycleSettledDate,
                            isCurrent: isCurrent,
                            depositAmount: Number(s.deposit_amount) || 0,
                            depositStatus: isCurrent ? depositStatus : (s.deposit_status || 'settled'),
                        });
                    });
                } else if (clientAssignments.length > 0) {
                    // Fall back to historical assignments ONLY if client has NO records in services table
                    const validAsgns = clientAssignments.filter(a => 
                        a.assignment_status !== 'cancelled' && 
                        !a.notes?.toLowerCase().includes('superseded')
                    );
                    const sortedAsgns = [...validAsgns].sort((a, b) => new Date(b.assigned_at || b.start_date || 0).getTime() - new Date(a.assigned_at || a.start_date || 0).getTime());
                    sortedAsgns.forEach(a => {
                        const isCurrent = activeAssignment ? a.id === activeAssignment.id : false;
                        const cycleStartDate = a.start_date || a.assigned_at || c.created_at;
                        const cycleEndDate = isCurrent ? (a.end_date || null) : (a.end_date || null);
                        const cycleSettledDate = isCurrent ? null : (cycleEndDate || a.end_date || a.assigned_at || c.created_at);

                        allServiceCycles.push({
                            id: a.id,
                            serviceType: formatServiceLabel(a.service_type, a.notes),
                            startDate: cycleStartDate,
                            endDate: cycleEndDate,
                            settledDate: cycleSettledDate,
                            isCurrent: isCurrent,
                            depositAmount: Number(a.deposit_amount) || 0,
                            depositStatus: isCurrent ? depositStatus : 'settled',
                        });
                    });
                }

                let pastDepositTotal = allServiceCycles
                    .filter(cyc => !cyc.isCurrent)
                    .reduce((sum, cyc) => sum + (Number(cyc.depositAmount) || 0), 0);

                // Fallback: If past cycles didn't explicitly store deposit, but payments ledger has recorded historical deposits
                // Only valid if client actually had a past completed/ended service or historical assignment
                const hasHistoricalService = clientServices.some(s => s.status !== 'active')
                    || clientAssignments.some(a => a.assignment_status === 'completed' || a.assignment_status === 'ended');
                const totalPaidDeposits = paidDepositByClientName[normalizeClientName(c.client_name)] || 0;
                if (pastDepositTotal <= 0 && hasHistoricalService && totalPaidDeposits > depositAmount) {
                    pastDepositTotal = totalPaidDeposits - depositAmount;
                }

                return {
                    id: c.id,
                    displayId: clientCodeMap.get(c.id) || `CL-${(c.id || '').substring(0, 4).toUpperCase()}`,
                    name: c.client_name,
                    phone: c.phone_number,
                    email: c.email || '-',
                    contact: c.client_name,
                    status: stageMap[c.id] || 'Active',
                    workerCount: workerMap[c.id]?.workerCount || 0,
                    activeWorkerCount: workerMap[c.id]?.activeWorkerCount || 0,
                    assignedCaregivers: workerMap[c.id]?.activeWorkerNames || (leadInfo?.assigned_worker_name ? [leadInfo.assigned_worker_name] : []),
                    hoursPerDay: activeService?.hours_per_day || (activeAssignment as any)?.hours_per_day || latestService?.hours_per_day || 10,
                    completeMonthDailyRate: activeService?.complete_month_daily_rate || leadInfo?.complete_month_daily_rate || 0,
                    incompleteMonthDailyRate: activeService?.incomplete_month_daily_rate || leadInfo?.incomplete_month_daily_rate || 0,
                    leadSource: c.source || leadInfo?.source || 'Manual Add',
                    lifetimeValue: '₹0',
                    securityDeposit: depositAmount,
                    depositStatus: depositStatus,
                    pastDepositTotal: pastDepositTotal,
                    allServiceCycles: allServiceCycles,
                    activeService: activeService || latestService,
                    activeAssignment: activeAssignment || latestAssignment,
                    created_at: c.created_at,
                    serviceStartDate: activeService?.start_date || activeAssignment?.start_date || latestService?.start_date || null,
                    city: extractCity({ ...c, ...leadInfo }),
                    services: clientServices,
                };
            });

            setClients(enrichedClients);
        } catch (error) {
            console.error('Error fetching client data:', error);
            toast.error('Failed to load dynamic client data');
        }
    };

    const fetchGoogleReviews = async () => {
        setIsLoadingGoogleReviews(true);
        try {
            const { data, error } = await supabase.functions.invoke('get-google-reviews');
            if (error) throw error;
            setGoogleReviews(data);
        } catch (error) {
            console.warn('Google reviews unavailable:', error);
            setGoogleReviews({ success: false, error: 'Unable to load Google reviews.' });
        } finally {
            setIsLoadingGoogleReviews(false);
        }
    };

    useEffect(() => {
        fetchClients();
        fetchGoogleReviews();
    }, []);

    useEffect(() => {
        if (restartClientId && clients.length > 0) {
            const target = clients.find(c => c.id === restartClientId);
            if (target) {
                openRestartModal(target);
            }
        }
    }, [restartClientId, clients]);

    return (
        <div className="p-4 sm:p-6 lg:p-8 flex flex-col space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">Client Master Database</h1>
                    <p className="text-slate-500 mt-1">Manage permanent clients, lifetime value, and automated review collection.</p>
                </div>
                <button 
                    onClick={() => navigate('/admin/billing?tab=history')}
                    className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-lg text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-all shadow-sm active:scale-95"
                >
                    <HistoryIcon className="w-4 h-4 text-primary" />
                    View Global Payment history
                </button>
            </div>

            {/* Client List (Full Width - Eliminates horizontal table scrolling) */}
            <div className="w-full bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden flex flex-col">
                {/* Header Toolbar: 2 structured tiers to prevent squishing or clipping on any window size */}
                <div className="p-4 border-b border-slate-200 bg-slate-50 flex flex-col gap-3">
                    {/* Tier 1: Search bar (full width / extended length) + View Toggle (Table / Cards) + View Trash */}
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
                        {/* Search Box - Extended length so placeholder is 100% visible */}
                        <div className="relative flex-1 min-w-[280px] sm:min-w-[360px] max-w-2xl">
                            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4 pointer-events-none" />
                            <input
                                type="text"
                                placeholder="Search by name, phone, or ID (e.g. CL-0037)..."
                                value={searchQuery}
                                onChange={e => setSearchQuery(e.target.value)}
                                className="w-full pl-10 pr-9 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all placeholder:text-slate-400 shadow-2xs"
                                title="Search clients by name, phone, or client ID"
                            />
                            {searchQuery && (
                                <button
                                    type="button"
                                    onClick={() => setSearchQuery('')}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-0.5 rounded cursor-pointer"
                                    title="Clear search"
                                >
                                    <X className="w-3.5 h-3.5" />
                                </button>
                            )}
                        </div>

                        {/* View Mode Toggle + Trash Button */}
                        <div className="flex items-center gap-2.5 shrink-0 self-end sm:self-auto">
                            <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5 shadow-2xs">
                                <button
                                    type="button"
                                    onClick={() => toggleViewMode('table')}
                                    className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-md transition-all cursor-pointer ${
                                        viewMode === 'table'
                                            ? 'bg-[#1AA6A8] text-white shadow-2xs'
                                            : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                                    }`}
                                    title="Compact table view with sortable columns"
                                >
                                    <Table className="w-3.5 h-3.5" />
                                    <span>Table</span>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => toggleViewMode('cards')}
                                    className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-md transition-all cursor-pointer ${
                                        viewMode === 'cards'
                                            ? 'bg-[#1AA6A8] text-white shadow-2xs'
                                            : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                                    }`}
                                    title="Expanded card view"
                                >
                                    <LayoutGrid className="w-3.5 h-3.5" />
                                    <span>Cards</span>
                                </button>
                            </div>

                            {/* Export CSV / Excel Button */}
                            <button
                                type="button"
                                onClick={() => handleExportCsv()}
                                className="px-3 py-1.5 text-xs font-bold text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 rounded-lg transition-colors flex items-center gap-1.5 shadow-2xs cursor-pointer"
                                title={selectedClientIds.size > 0 ? `Export ${selectedClientIds.size} selected clients to CSV / Excel` : `Export all ${sortedFilteredClients.length} clients in current view to CSV / Excel`}
                            >
                                <Download className="w-3.5 h-3.5 text-teal-600" />
                                <span className="hidden sm:inline">Export</span>
                                <span>CSV/Excel</span>
                                {selectedClientIds.size > 0 && (
                                    <span className="px-1.5 py-0.2 rounded-full text-[10px] font-bold bg-teal-100 text-teal-800 border border-teal-200">
                                        {selectedClientIds.size}
                                    </span>
                                )}
                            </button>

                            {/* Bulk Message Button if any selected */}
                            {selectedClientIds.size > 0 && (
                                <button
                                    type="button"
                                    onClick={() => setIsBulkMessageModalOpen(true)}
                                    className="px-3 py-1.5 text-xs font-bold text-white bg-[#1AA6A8] hover:bg-[#15898A] rounded-lg transition-colors flex items-center gap-1.5 shadow-2xs cursor-pointer animate-in fade-in"
                                    title="Send bulk WhatsApp message to selected clients"
                                >
                                    <MessageSquare className="w-3.5 h-3.5" />
                                    <span>Message ({selectedClientIds.size})</span>
                                </button>
                            )}

                            <button
                                onClick={() => setViewingTrash(!viewingTrash)}
                                className={`px-3 py-1.5 text-xs font-semibold rounded-lg transition-colors border shadow-2xs cursor-pointer ${
                                    viewingTrash 
                                        ? 'bg-red-50 text-red-600 border-red-200 font-bold' 
                                        : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                                }`}
                            >
                                {viewingTrash ? 'Exit Trash' : 'View Trash'}
                            </button>
                        </div>
                    </div>

                    {/* Tier 2: Period & Category Tabs + Month Navigator + City Filter */}
                    <div className="flex flex-wrap items-center justify-between gap-2.5 pt-2.5 border-t border-slate-200/70">

                            {/* All vs By Month segmented tabs + Select All Pill */}
                            <div className="flex items-center gap-2 flex-wrap">
                                {(() => {
                                    const activeClients = clients.filter(c => {
                                        if (c.status === 'Trash') return false;
                                        if (selectedCity !== 'all') {
                                            const city = c.city || extractCity(c);
                                            if (city.toLowerCase() !== selectedCity.toLowerCase()) return false;
                                        }
                                        return true;
                                    });
                                    const mCount = activeClients.filter(c => isClientInMonth(c, selectedMonth)).length;
                                    return (
                                        <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5 shadow-2xs">
                                            <button
                                                onClick={() => setViewAllMonths(true)}
                                                className={`px-3 py-1.5 text-xs font-bold rounded-md transition-all cursor-pointer ${
                                                    viewAllMonths
                                                        ? 'bg-[#1AA6A8] text-white shadow-2xs'
                                                        : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                                                }`}
                                            >
                                                All Clients ({activeClients.length})
                                            </button>
                                            <button
                                                onClick={() => setViewAllMonths(false)}
                                                className={`px-3 py-1.5 text-xs font-bold rounded-md transition-all cursor-pointer ${
                                                    !viewAllMonths
                                                        ? 'bg-[#1AA6A8] text-white shadow-2xs'
                                                        : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                                                }`}
                                            >
                                                By Month ({mCount})
                                            </button>
                                        </div>
                                    );
                                })()}

                                {/* Select All / Deselect All Button */}
                                {sortedFilteredClients.length > 0 && (
                                    <button
                                        type="button"
                                        onClick={() => handleToggleSelectAll(sortedFilteredClients)}
                                        className={`px-3 py-1.5 text-xs font-semibold rounded-lg border transition-all flex items-center gap-1.5 cursor-pointer shadow-2xs ${
                                            allVisibleSelected
                                                ? 'bg-teal-50 border-teal-300 text-teal-800 font-bold ring-2 ring-teal-200/50'
                                                : someVisibleSelected
                                                ? 'bg-teal-50/50 border-teal-200 text-teal-700 font-semibold'
                                                : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                                        }`}
                                        title={allVisibleSelected ? 'Deselect all visible clients' : 'Select all visible clients'}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={allVisibleSelected}
                                            ref={el => {
                                                if (el) el.indeterminate = someVisibleSelected && !allVisibleSelected;
                                            }}
                                            readOnly
                                            className="w-3.5 h-3.5 rounded text-teal-600 pointer-events-none accent-[#1AA6A8]"
                                        />
                                        <span>{allVisibleSelected ? 'Deselect All' : 'Select All'}</span>
                                        <span className="text-[10px] px-1.5 py-0.2 rounded-full bg-slate-100 text-slate-600 font-bold">
                                            {selectedClientIds.size > 0 ? `${selectedClientIds.size}/${sortedFilteredClients.length}` : sortedFilteredClients.length}
                                        </span>
                                    </button>
                                )}
                            </div>

                            {/* Right: Month Navigator + City Filter */}
                            <div className="flex items-center gap-2 flex-wrap">
                                {/* Month Navigator: ALWAYS VISIBLE */}
                                <div className={`flex items-center bg-white border rounded-lg overflow-hidden transition-all shadow-2xs ${
                                    !viewAllMonths 
                                        ? 'border-[#1AA6A8] ring-2 ring-[#1AA6A8]/20' 
                                        : 'border-slate-200 opacity-80 hover:opacity-100'
                                }`}>
                                    <button 
                                        onClick={() => {
                                            setViewAllMonths(false);
                                            prevMonth();
                                        }} 
                                        className="px-2.5 py-1.5 text-slate-600 hover:bg-slate-100 transition-colors font-bold text-sm cursor-pointer"
                                        title="Previous Month"
                                    >
                                        ‹
                                    </button>
                                    <span 
                                        onClick={() => setViewAllMonths(false)}
                                        className={`px-3 py-1.5 text-xs font-bold min-w-[125px] text-center border-x border-slate-200 select-none cursor-pointer transition-colors ${
                                            !viewAllMonths ? 'text-[#1AA6A8] bg-teal-50/50' : 'text-slate-700 hover:bg-slate-50'
                                        }`}
                                        title="Click to view by this month"
                                    >
                                        {monthLabel(selectedMonth)}
                                    </span>
                                    <button 
                                        onClick={() => {
                                            setViewAllMonths(false);
                                            nextMonth();
                                        }} 
                                        className="px-2.5 py-1.5 text-slate-600 hover:bg-slate-100 transition-colors font-bold text-sm cursor-pointer"
                                        title="Next Month"
                                    >
                                        ›
                                    </button>
                                </div>

                                {/* City Filter Dropdown */}
                                {(() => {
                                    const baseClients = clients.filter(c => {
                                        if (c.status === 'Trash') return false;
                                        if (!viewAllMonths && !isClientInMonth(c, selectedMonth)) return false;
                                        return true;
                                    });
                                    const counts: Record<string, number> = {};
                                    baseClients.forEach(c => {
                                        const city = c.city || extractCity(c);
                                        counts[city] = (counts[city] || 0) + 1;
                                    });
                                    const cityList = Object.keys(counts).sort((a, b) => counts[b] - counts[a]);
                                    if (!cityList.includes('Surat')) cityList.push('Surat');

                                    return (
                                        <div className="relative">
                                            <button
                                                onClick={() => setIsCityDropdownOpen(!isCityDropdownOpen)}
                                                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-lg border transition-all cursor-pointer ${
                                                    selectedCity !== 'all'
                                                        ? 'bg-sky-50 border-sky-300 text-sky-700 ring-2 ring-sky-200/50'
                                                        : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'
                                                }`}
                                            >
                                                <MapPin className="w-3.5 h-3.5 text-sky-600" />
                                                <span>{selectedCity === 'all' ? 'All Cities' : selectedCity}</span>
                                                <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform ${isCityDropdownOpen ? 'rotate-180' : ''}`} />
                                            </button>

                                            {isCityDropdownOpen && (
                                                <>
                                                    <div
                                                        className="fixed inset-0 z-30"
                                                        onClick={() => setIsCityDropdownOpen(false)}
                                                    />
                                                    <div className="absolute right-0 mt-1.5 w-52 max-h-72 overflow-y-auto bg-white rounded-xl shadow-xl border border-slate-200 py-1.5 z-50 animate-in fade-in zoom-in-95">
                                                        <div className="px-3 py-1 text-[10px] font-extrabold uppercase tracking-wider text-slate-400">
                                                            Filter by City
                                                        </div>
                                                        <button
                                                            onClick={() => {
                                                                setSelectedCity('all');
                                                                setIsCityDropdownOpen(false);
                                                            }}
                                                            className={`w-full text-left px-3 py-1.5 text-xs font-semibold flex items-center justify-between transition-colors cursor-pointer ${
                                                                selectedCity === 'all' ? 'bg-sky-50 text-sky-700 font-bold' : 'text-slate-700 hover:bg-slate-50'
                                                            }`}
                                                        >
                                                            <span>All Cities</span>
                                                            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500 font-bold">
                                                                {baseClients.length}
                                                            </span>
                                                        </button>
                                                        {cityList.map(cName => (
                                                            <button
                                                                key={cName}
                                                                onClick={() => {
                                                                    setSelectedCity(cName);
                                                                    setIsCityDropdownOpen(false);
                                                                }}
                                                                className={`w-full text-left px-3 py-1.5 text-xs font-semibold flex items-center justify-between transition-colors cursor-pointer ${
                                                                    selectedCity === cName ? 'bg-sky-50 text-sky-700 font-bold' : 'text-slate-700 hover:bg-slate-50'
                                                                }`}
                                                            >
                                                                <span className="flex items-center gap-1.5">
                                                                    <MapPin className="w-3 h-3 text-slate-400" />
                                                                    {cName}
                                                                </span>
                                                                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600 font-bold">
                                                                    {counts[cName] || 0}
                                                                </span>
                                                            </button>
                                                        ))}
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    );
                                })()}
                            </div>
                        </div>
                    </div>
                    <div className="flex-1 overflow-auto p-4 space-y-3">
                        {(() => {
                            if (sortedFilteredClients.length === 0) return (
                                <div className="flex flex-col items-center justify-center py-16 text-center">
                                    <Calendar className="w-10 h-10 text-slate-200 mb-3" />
                                    <p className="text-sm font-semibold text-slate-600">
                                        {viewingTrash 
                                            ? 'Trash is empty' 
                                            : searchQuery 
                                                ? 'No matching clients found' 
                                                : selectedCity !== 'all' 
                                                    ? `No clients found for ${selectedCity}${viewAllMonths ? '' : ' in ' + monthLabel(selectedMonth)}` 
                                                    : `No clients joined in ${monthLabel(selectedMonth)}`}
                                    </p>
                                    <p className="text-xs text-slate-400 mt-1 max-w-sm">
                                        {viewingTrash ? '' : searchQuery ? 'Try clearing your search query.' : `No new client registrations recorded in ${monthLabel(selectedMonth)}. You can view all clients or browse previous months.`}
                                    </p>
                                    {!viewAllMonths && !viewingTrash && (
                                        <div className="flex items-center gap-2 mt-4">
                                            <button
                                                onClick={() => {
                                                    setSelectedMonth('2026-08');
                                                }}
                                                className="px-3 py-1.5 text-xs font-bold text-teal-700 bg-teal-50 hover:bg-teal-100 border border-teal-200 rounded-lg transition-colors shadow-2xs"
                                            >
                                                Browse August 2026 (4 clients)
                                            </button>
                                            <button
                                                onClick={() => setViewAllMonths(true)}
                                                className="px-3.5 py-1.5 text-xs font-bold text-white bg-[#1AA6A8] hover:bg-[#15898A] rounded-lg transition-colors shadow-2xs"
                                            >
                                                View All Clients ({clients.filter(c => c.status !== 'Trash').length})
                                            </button>
                                        </div>
                                    )}
                                </div>
                            );

                            if (viewMode === 'table') {
                                return (
                                    <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-2xs">
                                        <table className="w-full text-left border-collapse text-xs">
                                            <thead>
                                                <tr className="bg-slate-50/90 border-b border-slate-200 text-[11px] font-extrabold uppercase tracking-wider text-slate-600 select-none">
                                            {/* Select All Checkbox */}
                                            <th className="py-2.5 px-3 w-10 text-center whitespace-nowrap">
                                                <div className="flex items-center justify-center">
                                                    <input
                                                        type="checkbox"
                                                        checked={allVisibleSelected}
                                                        ref={el => {
                                                            if (el) el.indeterminate = someVisibleSelected && !allVisibleSelected;
                                                        }}
                                                        onChange={() => handleToggleSelectAll(sortedFilteredClients)}
                                                        onClick={(e) => e.stopPropagation()}
                                                        className="w-4 h-4 rounded text-teal-600 focus:ring-teal-500 border-slate-300 cursor-pointer accent-[#1AA6A8]"
                                                        title={allVisibleSelected ? "Deselect all visible clients" : "Select all visible clients"}
                                                    />
                                                </div>
                                            </th>
                                            <th onClick={() => handleSort('displayId')} className="py-2.5 px-3 cursor-pointer hover:bg-slate-100 transition-colors whitespace-nowrap" title="Sort by Client ID">
                                                <div className="flex items-center gap-1">
                                                    <span>ID</span>
                                                    {sortField === 'displayId' ? (sortAsc ? <ArrowUp className="w-3 h-3 text-[#1AA6A8]" /> : <ArrowDown className="w-3 h-3 text-[#1AA6A8]" />) : <ArrowUpDown className="w-3 h-3 text-slate-300" />}
                                                </div>
                                            </th>
                                            <th onClick={() => handleSort('name')} className="py-2.5 px-3 cursor-pointer hover:bg-slate-100 transition-colors whitespace-nowrap" title="Sort by Name">
                                                <div className="flex items-center gap-1">
                                                    <span>Name</span>
                                                    {sortField === 'name' ? (sortAsc ? <ArrowUp className="w-3 h-3 text-[#1AA6A8]" /> : <ArrowDown className="w-3 h-3 text-[#1AA6A8]" />) : <ArrowUpDown className="w-3 h-3 text-slate-300" />}
                                                </div>
                                            </th>
                                            <th onClick={() => handleSort('city')} className="py-2.5 px-2.5 cursor-pointer hover:bg-slate-100 transition-colors whitespace-nowrap" title="Sort by City">
                                                <div className="flex items-center gap-1">
                                                    <span>City</span>
                                                    {sortField === 'city' ? (sortAsc ? <ArrowUp className="w-3 h-3 text-[#1AA6A8]" /> : <ArrowDown className="w-3 h-3 text-[#1AA6A8]" />) : <ArrowUpDown className="w-3 h-3 text-slate-300" />}
                                                </div>
                                            </th>
                                            <th onClick={() => handleSort('phone')} className="py-2.5 px-2.5 cursor-pointer hover:bg-slate-100 transition-colors whitespace-nowrap" title="Sort by Phone">
                                                <div className="flex items-center gap-1">
                                                    <span>Phone</span>
                                                    {sortField === 'phone' ? (sortAsc ? <ArrowUp className="w-3 h-3 text-[#1AA6A8]" /> : <ArrowDown className="w-3 h-3 text-[#1AA6A8]" />) : <ArrowUpDown className="w-3 h-3 text-slate-300" />}
                                                </div>
                                            </th>
                                            <th onClick={() => handleSort('workerCount')} className="py-2.5 px-2.5 cursor-pointer hover:bg-slate-100 transition-colors whitespace-nowrap" title="Sort by Workers">
                                                <div className="flex items-center gap-1">
                                                    <span>Workers</span>
                                                    {sortField === 'workerCount' ? (sortAsc ? <ArrowUp className="w-3 h-3 text-[#1AA6A8]" /> : <ArrowDown className="w-3 h-3 text-[#1AA6A8]" />) : <ArrowUpDown className="w-3 h-3 text-slate-300" />}
                                                </div>
                                            </th>
                                            <th onClick={() => handleSort('securityDeposit')} className="py-2.5 px-2.5 cursor-pointer hover:bg-slate-100 transition-colors whitespace-nowrap" title="Sort by Deposit">
                                                <div className="flex items-center gap-1">
                                                    <span>Deposit</span>
                                                    {sortField === 'securityDeposit' ? (sortAsc ? <ArrowUp className="w-3 h-3 text-[#1AA6A8]" /> : <ArrowDown className="w-3 h-3 text-[#1AA6A8]" />) : <ArrowUpDown className="w-3 h-3 text-slate-300" />}
                                                </div>
                                            </th>
                                            <th onClick={() => handleSort('status')} className="py-2.5 px-2.5 cursor-pointer hover:bg-slate-100 transition-colors whitespace-nowrap" title="Sort by Status">
                                                <div className="flex items-center gap-1">
                                                    <span>Status</span>
                                                    {sortField === 'status' ? (sortAsc ? <ArrowUp className="w-3 h-3 text-[#1AA6A8]" /> : <ArrowDown className="w-3 h-3 text-[#1AA6A8]" />) : <ArrowUpDown className="w-3 h-3 text-slate-300" />}
                                                </div>
                                            </th>
                                            <th onClick={() => handleSort('created_at')} className="py-2.5 px-2.5 cursor-pointer hover:bg-slate-100 transition-colors whitespace-nowrap" title="Service Start Date & Date Added to CRM">
                                                <div className="flex items-center gap-1">
                                                    <span>Date (Start / Added)</span>
                                                    {sortField === 'created_at' ? (sortAsc ? <ArrowUp className="w-3 h-3 text-[#1AA6A8]" /> : <ArrowDown className="w-3 h-3 text-[#1AA6A8]" />) : <ArrowUpDown className="w-3 h-3 text-slate-300" />}
                                                </div>
                                            </th>
                                            <th className="py-2.5 px-3 text-right whitespace-nowrap">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100 bg-white">
                                        {sortedFilteredClients.map((client) => {
                                            const isSelected = selectedClientIds.has(client.id);
                                            const city = client.city || extractCity(client);
                                            return (
                                                <tr
                                                    key={client.id}
                                                    onClick={() => setInspectingClient(client)}
                                                    className={`transition-colors cursor-pointer group ${
                                                        isSelected 
                                                            ? 'bg-teal-50/70 hover:bg-teal-100/60' 
                                                            : 'hover:bg-teal-50/30'
                                                    }`}
                                                >
                                                    {/* Row Checkbox */}
                                                    <td className="py-2.5 px-3 w-10 text-center whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                                                        <div className="flex items-center justify-center">
                                                            <input
                                                                type="checkbox"
                                                                checked={isSelected}
                                                                onChange={() => handleToggleSelect(client.id)}
                                                                onClick={(e) => e.stopPropagation()}
                                                                className="w-4 h-4 rounded text-teal-600 focus:ring-teal-500 border-slate-300 cursor-pointer accent-[#1AA6A8]"
                                                                title={`Select ${client.name}`}
                                                            />
                                                        </div>
                                                    </td>

                                                    {/* Client ID (CL-0037 with 1-click copy) */}
                                                    <td className="py-2.5 px-3 whitespace-nowrap">
                                                        <button
                                                            type="button"
                                                            onClick={(e) => handleCopyClientId(client.displayId || client.id, e)}
                                                            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-mono text-[11px] font-bold text-teal-800 bg-teal-50 border border-teal-200/80 hover:bg-teal-100 transition-colors cursor-pointer shadow-2xs"
                                                            title={`Click to copy ${client.displayId}\nFull UUID: ${client.id}`}
                                                        >
                                                            <span>{client.displayId || 'CL-0001'}</span>
                                                            {copiedClientIdBadge === (client.displayId || client.id) ? (
                                                                <Check className="w-2.5 h-2.5 text-emerald-600" />
                                                            ) : (
                                                                <Copy className="w-2.5 h-2.5 text-teal-500 opacity-60 group-hover:opacity-100" />
                                                            )}
                                                        </button>
                                                    </td>

                                                            {/* Name */}
                                                            <td className="py-2.5 px-3 whitespace-nowrap">
                                                                <div className="font-bold text-slate-900 group-hover:text-primary transition-colors">
                                                                    {client.name}
                                                                </div>
                                                            </td>

                                                            {/* City */}
                                                            <td className="py-2.5 px-2.5 whitespace-nowrap">
                                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-sky-50 text-sky-700 border border-sky-200 uppercase tracking-wider">
                                                                    <MapPin className="w-2.5 h-2.5 text-sky-600" /> {city}
                                                                </span>
                                                            </td>

                                                            {/* Phone */}
                                                            <td className="py-2.5 px-2.5 whitespace-nowrap">
                                                                {(() => {
                                                                    if (!client.phone) {
                                                                        return <span className="font-mono text-xs text-slate-400">N/A</span>;
                                                                    }

                                                                    const rawPhone = client.phone.trim();
                                                                    const cleanDigits = rawPhone.replace(/\D/g, '');
                                                                    const waDigits = cleanDigits.length === 10 ? `91${cleanDigits}` : cleanDigits;
                                                                    const telDigits = rawPhone.startsWith('+') ? rawPhone : (cleanDigits.length === 10 ? `+91${cleanDigits}` : `+${cleanDigits}`);
                                                                    const isCopied = copiedPhoneBadge === rawPhone;
                                                                    const displayPhone = maskPhoneNumber(rawPhone, canViewPhoneNumbers);

                                                                    if (!canViewPhoneNumbers) {
                                                                        return (
                                                                            <div className="inline-flex items-center gap-1.5 py-0.5">
                                                                                <span className="font-mono text-xs font-semibold text-slate-700 inline-flex items-center gap-1" title="Phone masked for role privacy">
                                                                                    <Lock className="w-3 h-3 text-slate-400 shrink-0" />
                                                                                    <span>{displayPhone}</span>
                                                                                </span>
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={(e) => handleCopyPhone(rawPhone, e)}
                                                                                    className="p-1 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors cursor-pointer"
                                                                                    title="Copy masked phone number"
                                                                                >
                                                                                    {isCopied ? (
                                                                                        <Check className="w-3.5 h-3.5 text-emerald-600" />
                                                                                    ) : (
                                                                                        <Copy className="w-3.5 h-3.5" />
                                                                                    )}
                                                                                </button>
                                                                            </div>
                                                                        );
                                                                    }

                                                                    return (
                                                                        <div className="inline-flex items-center gap-1.5 py-0.5">
                                                                            {/* Click-to-call link */}
                                                                            <a
                                                                                href={`tel:${telDigits}`}
                                                                                onClick={(e) => e.stopPropagation()}
                                                                                className="font-mono text-xs font-semibold text-slate-800 hover:text-primary hover:underline transition-colors inline-flex items-center gap-1 group/call"
                                                                                title={`Click to call ${rawPhone}`}
                                                                            >
                                                                                <Phone className="w-3 h-3 text-slate-400 group-hover/call:text-primary transition-colors shrink-0" />
                                                                                <span>{rawPhone}</span>
                                                                            </a>

                                                                            <div className="inline-flex items-center gap-0.5 shrink-0">
                                                                                {/* WhatsApp button */}
                                                                                <a
                                                                                    href={`https://wa.me/${waDigits}`}
                                                                                    target="_blank"
                                                                                    rel="noopener noreferrer"
                                                                                    onClick={(e) => e.stopPropagation()}
                                                                                    className="p-1 rounded-md text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 transition-colors cursor-pointer"
                                                                                    title={`Chat with ${client.name} on WhatsApp (${rawPhone})`}
                                                                                >
                                                                                    <MessageCircle className="w-3.5 h-3.5" />
                                                                                </a>

                                                                                {/* Copy phone button */}
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={(e) => handleCopyPhone(rawPhone, e)}
                                                                                    className="p-1 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors cursor-pointer"
                                                                                    title="Copy phone number"
                                                                                >
                                                                                    {isCopied ? (
                                                                                        <Check className="w-3.5 h-3.5 text-emerald-600" />
                                                                                    ) : (
                                                                                        <Copy className="w-3.5 h-3.5" />
                                                                                    )}
                                                                                </button>
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                })()}
                                                            </td>

                                                            {/* Workers */}
                                                            <td className="py-2.5 px-2.5 whitespace-nowrap">
                                                                <span className="inline-flex items-center gap-1 text-xs text-slate-600">
                                                                    <Users className="w-3 h-3 text-slate-400" />
                                                                    <strong className="text-slate-800">{client.activeWorkerCount}</strong>
                                                                    <span className="text-slate-400">/{client.workerCount}</span>
                                                                </span>
                                                            </td>

                                                            {/* Deposit */}
                                                            <td className="py-2.5 px-2.5 whitespace-nowrap">
                                                                {(() => {
                                                                    const { settledDateStr } = getClientDepositBreakdown(client);
                                                                    return (
                                                                        <div className="flex flex-col">
                                                                            <div className="flex items-center gap-1.5">
                                                                                <span className={`font-bold text-xs ${
                                                                                    client.depositStatus === 'pending'
                                                                                        ? 'text-amber-700'
                                                                                        : (client.depositStatus === 'settled' ? 'text-slate-700' : 'text-emerald-700')
                                                                                }`}>
                                                                                    {client.activeService || client.activeAssignment ? `₹${client.securityDeposit.toLocaleString('en-IN')}` : '₹0'}
                                                                                </span>
                                                                                <span className={`text-[9px] font-extrabold uppercase px-1.5 py-0.2 rounded-full border ${
                                                                                    client.depositStatus === 'pending'
                                                                                        ? 'bg-amber-100 text-amber-800 border-amber-300'
                                                                                        : (client.depositStatus === 'settled'
                                                                                            ? 'bg-slate-200 text-slate-700 border-slate-300'
                                                                                            : 'bg-emerald-100 text-emerald-800 border-emerald-200')
                                                                                }`}>
                                                                                    {client.depositStatus === 'pending' ? 'Pending' : (client.depositStatus === 'settled' ? 'Settled' : 'Paid')}
                                                                                </span>
                                                                            </div>
                                                                            {client.pastDepositTotal > 0 && (
                                                                                <div className="text-[10px] text-slate-500 font-medium flex items-center gap-1 mt-0.5" title={`Refunded/Settled: ₹${client.pastDepositTotal.toLocaleString('en-IN')}${settledDateStr ? ` on ${settledDateStr}` : ''}`}>
                                                                                    <span className="text-slate-400">Settled:</span>
                                                                                    <span className="font-semibold text-slate-700">₹{client.pastDepositTotal.toLocaleString('en-IN')}</span>
                                                                                    {settledDateStr && <span className="text-slate-400 text-[9px]">({settledDateStr})</span>}
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    );
                                                                })()}
                                                            </td>

                                                            {/* Status */}
                                                            <td className="py-2.5 px-2.5 whitespace-nowrap">
                                                                <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${
                                                                    client.status === 'Closed Won' ? 'bg-emerald-100 text-emerald-700' :
                                                                    client.status === 'Monthly Billing' ? 'bg-blue-100 text-blue-700' :
                                                                    client.status === 'Active Client' ? 'bg-teal-100 text-teal-700' :
                                                                    client.status === 'Archived' ? 'bg-slate-100 text-slate-500' :
                                                                    client.status === 'Trash' ? 'bg-red-100 text-red-600' :
                                                                    'bg-slate-100 text-slate-600'
                                                                }`}>
                                                                    {client.status === 'Closed Won' ? 'Closed' :
                                                                     client.status === 'Monthly Billing' ? 'Billing' :
                                                                     client.status === 'Active Client' ? 'Active' :
                                                                     client.status === 'Archived' ? 'Archived' :
                                                                     client.status === 'Trash' ? 'Trash' :
                                                                     client.status}
                                                                </span>
                                                            </td>

                                                            {/* Date (Service Start & Date Added) */}
                                                            <td className="py-2.5 px-2.5 whitespace-nowrap">
                                                                <div className="flex flex-col">
                                                                    {client.serviceStartDate ? (
                                                                        <>
                                                                            <span className="text-xs font-semibold text-slate-800" title={`Service start date: ${formatDateDisplay(client.serviceStartDate)}`}>
                                                                                {formatDateDisplay(client.serviceStartDate)}
                                                                            </span>
                                                                            <span className="text-[10px] text-slate-400" title={`Client added to database: ${formatDateDisplay(client.created_at)}`}>
                                                                                Added {formatDateDisplay(client.created_at)}
                                                                            </span>
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <span className="text-xs font-semibold text-slate-800" title={`Client added to database: ${formatDateDisplay(client.created_at)}`}>
                                                                                {formatDateDisplay(client.created_at)}
                                                                            </span>
                                                                            <span className="text-[10px] text-slate-400">
                                                                                Date Added
                                                                            </span>
                                                                        </>
                                                                    )}
                                                                </div>
                                                            </td>

                                                            {/* Actions */}
                                                            <td className="py-2.5 px-3 text-right whitespace-nowrap">
                                                                <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => setInspectingClient(client)}
                                                                        className="p-1 text-slate-500 hover:text-primary hover:bg-primary/10 rounded-lg transition-colors cursor-pointer"
                                                                        title="View full client details"
                                                                    >
                                                                        <Eye className="w-3.5 h-3.5" />
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => openDepositModal(client)}
                                                                        className="p-1 text-slate-500 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors cursor-pointer"
                                                                        title={client.depositStatus === 'pending' ? 'Collect deposit' : 'Deposit history'}
                                                                    >
                                                                        <Receipt className="w-3.5 h-3.5" />
                                                                    </button>
                                                                    {client.status === 'Closed Won' && (
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => openRestartModal(client)}
                                                                            className="p-1 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 rounded-lg transition-colors cursor-pointer"
                                                                            title="Restart service"
                                                                        >
                                                                            <RotateCcw className="w-3.5 h-3.5" />
                                                                        </button>
                                                                    )}
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleRemoveFromPipeline(client)}
                                                                        className={`p-1 rounded-lg transition-colors cursor-pointer ${
                                                                            client.status === 'Archived'
                                                                                ? 'text-emerald-600 hover:bg-emerald-50'
                                                                                : 'text-slate-400 hover:text-red-500 hover:bg-red-50'
                                                                        }`}
                                                                        title={client.status === 'Archived' ? 'Reactivate (add back to pipeline)' : 'Archive client (remove from pipeline)'}
                                                                    >
                                                                        {client.status === 'Archived' ? (
                                                                            <Plus className="w-3.5 h-3.5" />
                                                                        ) : (
                                                                            <UserMinus className="w-3.5 h-3.5" />
                                                                        )}
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                        <div className="p-2.5 bg-slate-50/80 border-t border-slate-200 text-xs text-slate-500 flex items-center justify-between flex-wrap gap-2">
                                            <div className="flex items-center gap-2">
                                                <span>Showing {sortedFilteredClients.length} client{sortedFilteredClients.length !== 1 ? 's' : ''}</span>
                                                {selectedClientIds.size > 0 && (
                                                    <span className="font-semibold text-teal-700 bg-teal-50 px-2 py-0.5 rounded-full border border-teal-200 text-[11px]">
                                                        {selectedClientIds.size} selected
                                                    </span>
                                                )}
                                            </div>
                                            <div className="flex items-center gap-3">
                                                {selectedClientIds.size > 0 ? (
                                                    <button
                                                        type="button"
                                                        onClick={handleClearSelection}
                                                        className="text-xs font-semibold text-slate-500 hover:text-red-600 cursor-pointer"
                                                    >
                                                        Clear Selection
                                                    </button>
                                                ) : (
                                                    <button
                                                        type="button"
                                                        onClick={() => handleToggleSelectAll(sortedFilteredClients)}
                                                        className="text-xs font-semibold text-teal-600 hover:text-teal-700 cursor-pointer"
                                                    >
                                                        Select All Visible
                                                    </button>
                                                )}
                                                <span className="text-[11px] text-slate-400">Click any row to open full client dossier</span>
                                            </div>
                                        </div>
                                    </div>
                                );
                            }

                            // 2. Expanded Card View (Issue 1, with fixes for Issues 2, 4, 5)
                            return sortedFilteredClients.map((client, idx) => {
                                const isSelected = selectedClientIds.has(client.id);
                                return (
                                <div
                                    key={client.id}
                                    onClick={() => setInspectingClient(client)}
                                    className={`p-4 rounded-lg border transition-all group cursor-pointer ${
                                        isSelected
                                            ? 'border-[#1AA6A8] bg-teal-50/20 ring-2 ring-[#1AA6A8]/20 shadow-sm'
                                            : 'border-slate-200 bg-white hover:border-primary/30 hover:shadow-sm'
                                    }`}
                                >
                                    {/* 1. Card Top Bar: Checkbox, # Serial Number, Client ID (CL-0037) & Scope on Left + Added Date & Status on Right */}
                                    <div className="flex items-center justify-between gap-2 pb-2.5 mb-3 border-b border-slate-100 flex-wrap">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <input
                                                type="checkbox"
                                                checked={isSelected}
                                                onChange={() => handleToggleSelect(client.id)}
                                                onClick={(e) => e.stopPropagation()}
                                                className="w-4 h-4 rounded text-teal-600 focus:ring-teal-500 border-slate-300 cursor-pointer accent-[#1AA6A8] mr-0.5"
                                                title={`Select ${client.name}`}
                                            />
                                            <span className="font-mono font-bold text-slate-700 bg-slate-100 px-2 py-0.5 rounded text-[11px] border border-slate-200/90 shadow-2xs" title={`Client #${idx + 1} in current view`}>
                                                #{idx + 1}
                                            </span>
                                            {client.id && (
                                                <button
                                                    type="button"
                                                    onClick={(e) => handleCopyClientId(client.displayId || client.id, e)}
                                                    className="inline-flex items-center gap-1.5 font-mono text-xs font-bold text-teal-800 bg-teal-50 hover:bg-teal-100 px-2.5 py-0.5 rounded-md border border-teal-200/80 transition-all cursor-pointer group shadow-2xs"
                                                    title={`Full Client UUID: ${client.id}\nClick to copy ${client.displayId || 'Client ID'}`}
                                                >
                                                    <span className="text-teal-600 text-[10px] font-sans font-semibold">ID:</span>
                                                    <span className="font-bold text-teal-900">{client.displayId || 'CL-0001'}</span>
                                                    {copiedClientIdBadge === (client.displayId || client.id) ? (
                                                        <Check className="w-3 h-3 text-emerald-600 shrink-0" />
                                                    ) : (
                                                        <Copy className="w-3 h-3 text-teal-500 group-hover:text-teal-700 shrink-0" />
                                                    )}
                                                </button>
                                            )}
                                            <span className="text-slate-300">•</span>
                                            <span className="text-[11px] text-slate-500 font-medium">
                                                {idx + 1} of {sortedFilteredClients.length} clients
                                            </span>
                                        </div>

                                        <div className="flex items-center gap-2.5">
                                        {client.created_at && (
                                            <span className="text-[10px] text-slate-400 hidden sm:inline-block">
                                                Added {new Date(client.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                                            </span>
                                        )}
                                        <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                                            client.status === 'Closed Won' ? 'bg-emerald-100 text-emerald-700' :
                                            client.status === 'Monthly Billing' ? 'bg-blue-100 text-blue-700' :
                                            client.status === 'Active Client' ? 'bg-teal-100 text-teal-700' :
                                            client.status === 'Archived' ? 'bg-slate-100 text-slate-500' :
                                            client.status === 'Trash' ? 'bg-red-100 text-red-600' :
                                            'bg-slate-100 text-slate-600'
                                        }`}>
                                            {client.status === 'Closed Won' ? 'Closed' :
                                             client.status === 'Monthly Billing' ? 'Billing' :
                                             client.status === 'Active Client' ? 'Active' :
                                             client.status === 'Archived' ? 'Archived' :
                                             client.status === 'Trash' ? 'Trash' :
                                             client.status}
                                        </span>
                                    </div>
                                </div>

                                {/* 2. Client Main Header: Building Icon + Name + City + Workers */}
                                <div className="flex items-center gap-3 mb-3">
                                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                                        <Building className="w-5 h-5 text-primary" />
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <h3 className="font-bold text-slate-900 group-hover:text-primary transition-colors">{client.name}</h3>
                                            {(() => {
                                                const city = client.city || extractCity(client);
                                                return (
                                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-sky-50 text-sky-700 border border-sky-200 uppercase tracking-wider">
                                                        <MapPin className="w-2.5 h-2.5 text-sky-600" /> {city}
                                                    </span>
                                                );
                                            })()}
                                        </div>
                                        <p className="text-sm text-slate-500 flex items-center gap-1 mt-0.5">
                                            <Users className="w-3.5 h-3.5" /> {client.activeWorkerCount} Active / {client.workerCount} Total Workers
                                        </p>
                                    </div>
                                </div>

                                {/* 3. Detail Columns */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mt-4 pt-4 border-t border-slate-100">
                                    <div className="bg-slate-50 p-2.5 rounded-xl border border-slate-100 flex flex-col justify-center">
                                        <div className="flex items-center justify-between gap-1 mb-1">
                                            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider flex items-center gap-1">
                                                <Phone className="w-3 h-3" /> Contact
                                            </p>
                                            {client.phone && (
                                                <div className="flex items-center gap-0.5">
                                                    {/* WhatsApp button - only for users with full phone permission */}
                                                    {canViewPhoneNumbers && (
                                                        <a
                                                            href={`https://wa.me/${(client.phone.replace(/\D/g, '').length === 10 ? '91' : '') + client.phone.replace(/\D/g, '')}`}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            onClick={(e) => e.stopPropagation()}
                                                            className="p-1 rounded-md text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 transition-colors cursor-pointer"
                                                            title={`Chat on WhatsApp (${client.phone})`}
                                                        >
                                                            <MessageCircle className="w-3.5 h-3.5" />
                                                        </a>
                                                    )}
                                                    {/* Copy button */}
                                                    <button
                                                        type="button"
                                                        onClick={(e) => handleCopyPhone(client.phone, e)}
                                                        className="p-1 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-200/60 transition-colors cursor-pointer"
                                                        title={canViewPhoneNumbers ? "Copy phone number" : "Copy masked phone"}
                                                    >
                                                        {copiedPhoneBadge === client.phone ? (
                                                            <Check className="w-3.5 h-3.5 text-emerald-600" />
                                                        ) : (
                                                            <Copy className="w-3.5 h-3.5" />
                                                        )}
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                        {client.phone ? (
                                            canViewPhoneNumbers ? (
                                                <a
                                                    href={`tel:${client.phone.startsWith('+') ? client.phone : (client.phone.replace(/\D/g, '').length === 10 ? `+91${client.phone.replace(/\D/g, '')}` : `+${client.phone.replace(/\D/g, '')}`)}`}
                                                    onClick={(e) => e.stopPropagation()}
                                                    className="text-sm font-bold text-slate-700 hover:text-primary hover:underline truncate transition-colors font-mono inline-flex items-center gap-1 group/call"
                                                    title={`Click to call ${client.phone}`}
                                                >
                                                    <Phone className="w-3 h-3 text-slate-400 group-hover/call:text-primary transition-colors shrink-0" />
                                                    <span>{client.phone}</span>
                                                </a>
                                            ) : (
                                                <div className="text-sm font-bold text-slate-700 truncate font-mono inline-flex items-center gap-1" title="Phone masked for role privacy">
                                                    <Lock className="w-3 h-3 text-slate-400 shrink-0" />
                                                    <span>{maskPhoneNumber(client.phone, false)}</span>
                                                </div>
                                            )
                                        ) : (
                                            <p className="text-sm font-bold text-slate-400 truncate">N/A</p>
                                        )}
                                    </div>

                                    {/* Issue 2 Fix: Deposit Box with dedicated action row to prevent horizontal overflow */}
                                    <div className={`p-2.5 rounded-xl border flex flex-col justify-between transition-all ${
                                        client.depositStatus === 'pending'
                                            ? 'bg-amber-50/60 border-amber-200'
                                            : (client.depositStatus === 'settled' ? 'bg-slate-50 border-slate-200' : 'bg-emerald-50/50 border-emerald-100')
                                    }`}>
                                        {(() => {
                                            const { currentDepositDateStr, settledDateStr, pastServiceName } = getClientDepositBreakdown(client);
                                            const hasActive = Boolean(client.activeService || client.activeAssignment);
                                            return (
                                                <div className="space-y-1.5">
                                                    <div className="flex items-center justify-between gap-1 pb-1 border-b border-slate-200/60">
                                                        <p className={`text-[10px] font-bold uppercase tracking-wider flex items-center gap-1 truncate ${
                                                            client.depositStatus === 'pending' 
                                                                ? 'text-amber-700' 
                                                                : (client.depositStatus === 'settled' ? 'text-slate-700' : 'text-emerald-700')
                                                        }`}>
                                                            <Wallet className="w-3 h-3 shrink-0" /> Security Deposit
                                                        </p>
                                                        <span className={`text-[9px] font-extrabold uppercase px-1.5 py-0.2 rounded-full border shrink-0 ${
                                                            client.depositStatus === 'pending'
                                                                ? 'bg-amber-100 text-amber-800 border-amber-300'
                                                                : (client.depositStatus === 'settled'
                                                                    ? 'bg-slate-200 text-slate-700 border-slate-300'
                                                                    : 'bg-emerald-100 text-emerald-800 border-emerald-200')
                                                        }`}>
                                                            {client.depositStatus === 'pending' ? 'Pending' : (client.depositStatus === 'settled' ? 'Settled' : 'Paid')}
                                                        </span>
                                                    </div>

                                                    {/* Current Deposit */}
                                                    <div>
                                                        <div className="flex items-baseline justify-between gap-1">
                                                            <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wide">
                                                                Current Deposit:
                                                            </span>
                                                            <span className={`text-xs font-extrabold ${
                                                                client.depositStatus === 'pending' 
                                                                    ? 'text-amber-800' 
                                                                    : (hasActive ? 'text-emerald-700' : 'text-slate-600')
                                                            }`}>
                                                                {hasActive ? `₹${client.securityDeposit.toLocaleString('en-IN')}` : '₹0'}
                                                            </span>
                                                        </div>
                                                        {hasActive ? (
                                                            currentDepositDateStr && (
                                                                <p className="text-[10px] text-slate-400">
                                                                    Since {currentDepositDateStr}
                                                                </p>
                                                            )
                                                        ) : (
                                                            <p className="text-[10px] text-slate-400">
                                                                No active service
                                                            </p>
                                                        )}
                                                    </div>

                                                    {/* Refunded / Settled Deposit */}
                                                    <div className="pt-1 border-t border-slate-200/50">
                                                        <div className="flex items-baseline justify-between gap-1">
                                                            <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wide">
                                                                Refunded / Settled:
                                                            </span>
                                                            <span className="text-xs font-bold text-slate-700">
                                                                {client.pastDepositTotal > 0 ? `₹${client.pastDepositTotal.toLocaleString('en-IN')}` : '₹0'}
                                                            </span>
                                                        </div>
                                                        {client.pastDepositTotal > 0 && settledDateStr && (
                                                            <p className="text-[10px] text-slate-500 flex items-center gap-1 mt-0.5 truncate" title={`Settled on ${settledDateStr} upon completion of ${pastServiceName}`}>
                                                                <HistoryIcon className="w-2.5 h-2.5 text-slate-400 shrink-0" />
                                                                <span>Settled on {settledDateStr}</span>
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })()}

                                        {/* Dedicated Action Row inside box - never overflows card edge */}
                                        <div className="mt-2 pt-1.5 border-t border-slate-200/50">
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    openDepositModal(client);
                                                }}
                                                className={`w-full text-[10px] font-bold py-1 px-2 rounded-lg border flex items-center justify-center gap-1 transition-colors cursor-pointer ${
                                                    client.depositStatus === 'pending'
                                                        ? 'text-amber-800 bg-amber-100/90 hover:bg-amber-200 border-amber-300'
                                                        : 'text-slate-700 bg-white/90 hover:bg-white border-slate-200 shadow-2xs'
                                                }`}
                                                title="View deposit history, send invoice, or record payment"
                                            >
                                                <Receipt className="w-3 h-3" />
                                                <span>{client.depositStatus === 'pending' ? 'Collect Deposit' : 'Deposit History'}</span>
                                            </button>
                                        </div>
                                    </div>

                                    {/* Action buttons */}
                                    <div className="col-span-1 sm:col-span-2 lg:col-span-2 flex items-center gap-2 flex-wrap">
                                        {client.status === 'Trash' ? (
                                            <>
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); handleRestoreClient(client); }}
                                                    className="flex-1 px-3 py-2 text-xs font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded-xl hover:bg-amber-100 transition-all flex items-center justify-center gap-1.5 shadow-sm"
                                                >
                                                    <ArchiveRestore className="w-3.5 h-3.5" /> Restore Client
                                                </button>
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); handleDeleteClientClick(client); }}
                                                    className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all border border-transparent hover:border-red-100"
                                                    title="Delete permanently"
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </>
                                        ) : (
                                            <>
                                                <button
                                                    onClick={(e) => { e.stopPropagation(); handleRequestReview(client); }}
                                                    className={`flex-1 min-w-[100px] px-3 py-2 text-xs font-bold rounded-xl transition-all flex items-center justify-center gap-1.5 shadow-sm ${
                                                        reviewSentIds.has(client.id)
                                                            ? 'text-slate-500 bg-slate-100 border border-slate-200 hover:bg-slate-200'
                                                            : 'text-slate-600 bg-white border border-slate-200 hover:bg-slate-50'
                                                    }`}
                                                >
                                                    <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500" />
                                                    {reviewSentIds.has(client.id) ? 'Resend Review' : 'Review'}
                                                </button>
                                                {client.depositStatus === 'pending' ? (
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); openDepositModal(client); }}
                                                        className="px-3 py-2 text-xs font-bold text-amber-800 bg-amber-50 border border-amber-200 rounded-xl hover:bg-amber-100 transition-all flex items-center justify-center gap-1.5 shadow-xs"
                                                        title="Share deposit invoice or record payment"
                                                    >
                                                        <Receipt className="w-3.5 h-3.5 text-amber-600" /> Deposit Invoice
                                                    </button>
                                                ) : client.pastDepositTotal > 0 && (
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); openDepositModal(client); }}
                                                        className="px-2.5 py-2 text-xs font-semibold text-slate-600 bg-slate-50 border border-slate-200 rounded-xl hover:bg-slate-100 transition-all flex items-center justify-center gap-1.5 shadow-2xs"
                                                        title="View complete deposit & service history"
                                                    >
                                                        <HistoryIcon className="w-3.5 h-3.5 text-slate-400" /> Deposit History
                                                    </button>
                                                )}
                                                {client.status === 'Closed Won' && (
                                                    <button
                                                        onClick={(e) => { e.stopPropagation(); openRestartModal(client); }}
                                                        className="flex-1 px-3 py-2 text-xs font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl hover:bg-emerald-100 transition-all flex items-center justify-center gap-1.5 shadow-sm"
                                                    >
                                                        <RotateCcw className="w-3.5 h-3.5" /> Restart Service
                                                    </button>
                                                )}
                                                {/* Issue 4 Fix: Labeled Button with Tooltip and Confirmation */}
                                                <button
                                                    type="button"
                                                    onClick={(e) => { e.stopPropagation(); handleRemoveFromPipeline(client); }}
                                                    className={`px-2.5 py-2 rounded-xl transition-all border text-xs font-bold flex items-center justify-center gap-1.5 shadow-2xs cursor-pointer ${
                                                        client.status === 'Archived'
                                                            ? 'text-emerald-700 bg-emerald-50 border-emerald-200 hover:bg-emerald-100'
                                                            : 'text-slate-600 bg-slate-50 border-slate-200 hover:text-red-600 hover:bg-red-50 hover:border-red-200'
                                                    }`}
                                                    title={client.status === 'Archived' ? 'Reactivate client (return to CRM pipeline)' : 'Archive / Deactivate client from pipeline'}
                                                >
                                                    {client.status === 'Archived' ? (
                                                        <>
                                                            <Plus className="w-3.5 h-3.5 text-emerald-600" />
                                                            <span>Reactivate</span>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <UserMinus className="w-3.5 h-3.5 text-slate-500" />
                                                            <span>Archive</span>
                                                        </>
                                                    )}
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    });
                        })()}
                    </div>
                </div>

            {/* Bottom Section: Client Automations & Google Reviews (Pushed to bottom as 2-column grid) */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Client Automations */}
                <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden flex flex-col">
                    <div className="p-5 border-b border-slate-100">
                        <h2 className="font-bold text-slate-900">Client Automations</h2>
                        <p className="text-sm text-slate-500 mt-1">Post-conversion workflows.</p>
                    </div>
                    <div className="p-5 space-y-4">
                        <div className={`p-4 rounded-lg border transition-colors ${workflows.reviewCollection ? 'border-amber-300 bg-amber-50' : 'border-slate-200'}`}>
                            <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                    <Star className={`w-5 h-5 ${workflows.reviewCollection ? 'text-amber-600' : 'text-slate-400'}`} />
                                    <h3 className={`font-semibold ${workflows.reviewCollection ? 'text-amber-700' : 'text-slate-600'}`}>Auto-Review Collection</h3>
                                </div>
                                <button
                                    onClick={() => toggleWorkflow('reviewCollection')}
                                    className={`w-10 h-5 rounded-full relative cursor-pointer transition-colors ${workflows.reviewCollection ? 'bg-amber-500' : 'bg-slate-200'}`}
                                >
                                    <div className={`w-4 h-4 rounded-full bg-white absolute top-0.5 shadow-sm transition-all ${workflows.reviewCollection ? 'right-0.5' : 'left-0.5'}`}></div>
                                </button>
                            </div>
                            <p className="text-sm text-slate-500">Automatically sends a feedback request to clients 14 days after joining, requesting a review.</p>
                        </div>
                    </div>
                </div>

                {/* Google Reviews */}
                <div className="bg-slate-50 rounded-xl border border-slate-200 overflow-hidden flex flex-col justify-between">
                    <div>
                        <div className="p-4 border-b border-slate-200 bg-white flex items-center justify-between">
                            <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                                <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
                                Google Reviews
                            </h3>
                            <a href={googleReviews?.googleMapsUri || GOOGLE_MAPS_URL} target="_blank" rel="noopener noreferrer"
                                className="text-xs font-bold text-primary hover:underline">
                                View All →
                            </a>
                        </div>
                        <div className="p-5 space-y-3">
                            <div className="flex items-center gap-3 p-4 bg-white rounded-xl border border-slate-200">
                                <div className="w-12 h-12 bg-amber-50 rounded-xl flex items-center justify-center shrink-0">
                                    <Star className="w-6 h-6 text-amber-400 fill-amber-400" />
                                </div>
                                <div className="flex-1">
                                    <p className="font-bold text-slate-900 text-sm">{googleReviews?.displayName || '99 Care'} — Google Reviews</p>
                                    <div className="flex items-center gap-2 mt-1">
                                        {isLoadingGoogleReviews ? (
                                            <span className="text-xs text-slate-500">Loading live reviews...</span>
                                        ) : googleReviews?.success ? (
                                            <>
                                                <span className="text-sm font-bold text-slate-900">{Number(googleReviews.rating || 0).toFixed(1)}</span>
                                                <span className="flex items-center gap-0.5">
                                                    {[1, 2, 3, 4, 5].map((star) => (
                                                        <Star
                                                            key={star}
                                                            className={`w-3.5 h-3.5 ${star <= Math.round(googleReviews.rating || 0) ? 'text-amber-400 fill-amber-400' : 'text-slate-200 fill-slate-200'}`}
                                                        />
                                                    ))}
                                                </span>
                                                <span className="text-xs text-slate-500">{googleReviews.userRatingCount || 0} reviews</span>
                                            </>
                                        ) : (
                                            <span className="text-xs text-slate-500">Connect Google API key to show live reviews</span>
                                        )}
                                    </div>
                                </div>
                                <a href={googleReviews?.googleMapsUri || GOOGLE_MAPS_URL} target="_blank" rel="noopener noreferrer"
                                    className="px-3 py-1.5 bg-primary text-white text-xs font-bold rounded-lg hover:bg-primary/90 transition-colors shrink-0">
                                    Open
                                </a>
                            </div>

                            {googleReviews?.success && googleReviews.reviews?.length > 0 ? (
                                <div className="space-y-3">
                                    {googleReviews.reviews.slice(0, 3).map((review: any, index: number) => (
                                        <div key={`${review.publishTime || index}-${review.name}`} className="p-4 bg-white rounded-xl border border-slate-200">
                                            <div className="flex items-start gap-3">
                                                {review.photoUri ? (
                                                    <img src={review.photoUri} alt={review.name} className="w-9 h-9 rounded-full object-cover" referrerPolicy="no-referrer" />
                                                ) : (
                                                    <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center text-xs font-bold text-slate-500">
                                                        {String(review.name || 'G').charAt(0)}
                                                    </div>
                                                )}
                                                <div className="min-w-0 flex-1">
                                                    <div className="flex items-center justify-between gap-2">
                                                        <p className="text-sm font-bold text-slate-900 truncate">{review.name}</p>
                                                        <span className="text-[10px] text-slate-400 shrink-0">{review.relativePublishTimeDescription}</span>
                                                    </div>
                                                    <div className="flex items-center gap-0.5 mt-1">
                                                        {[1, 2, 3, 4, 5].map((star) => (
                                                            <Star
                                                                key={star}
                                                                className={`w-3 h-3 ${star <= Number(review.rating || 0) ? 'text-amber-400 fill-amber-400' : 'text-slate-200 fill-slate-200'}`}
                                                            />
                                                        ))}
                                                    </div>
                                                    {review.text && (
                                                        <p className="text-xs text-slate-600 leading-relaxed mt-2 line-clamp-3">"{review.text}"</p>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-[11px] text-slate-400 text-center">
                                    {googleReviews?.error || 'Live Google reviews will appear here after the Google Places API key is configured.'}
                                </p>
                            )}
                        </div>
                    </div>
                    <div className="p-3 bg-white border-t border-slate-100 flex items-center justify-between">
                        <p className="text-xs text-slate-500">Tap "View All" to see all Google reviews</p>
                        <a href={GOOGLE_REVIEW_URL} target="_blank" rel="noopener noreferrer"
                            className="px-3 py-1.5 bg-amber-50 text-amber-700 border border-amber-200 text-xs font-bold rounded-lg hover:bg-amber-100 transition-colors flex items-center gap-1.5">
                            <Star className="w-3.5 h-3.5 fill-amber-500 text-amber-500" /> Leave a Review
                        </a>
                    </div>
                </div>
            </div>


            {/* Comprehensive Restart Service Modal */}
            {restartModal && (
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4 z-50">
                    <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[92vh] flex flex-col shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
                        {/* Header */}
                        <div className="p-4 sm:p-5 border-b border-slate-100 bg-gradient-to-r from-emerald-50/80 via-teal-50/40 to-white flex justify-between items-center shrink-0">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-emerald-100 text-emerald-700 rounded-xl flex items-center justify-center shadow-xs">
                                    <RotateCcw className="w-5 h-5" />
                                </div>
                                <div>
                                    <h2 className="text-base sm:text-lg font-bold text-slate-900">Restart Service Onboarding</h2>
                                    <div className="flex items-center gap-2 text-xs text-slate-500 font-medium mt-0.5">
                                        <span className="font-bold text-slate-800">{restartModal.name}</span>
                                        {restartModal.phone && (
                                            <>
                                                <span>•</span>
                                                <span className="flex items-center gap-1"><Phone className="w-3 h-3 text-slate-400" />{restartModal.phone}</span>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <button 
                                onClick={() => setRestartModal(null)} 
                                className="text-slate-400 hover:text-slate-600 p-2 rounded-lg hover:bg-slate-100 transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Modal Body: Scrollable */}
                        <div className="p-4 sm:p-6 overflow-y-auto space-y-6 flex-1 text-slate-800">
                            {/* 1. Service Selection & Shift Duration */}
                            <div className="space-y-3">
                                <div className="flex items-center gap-2 pb-1.5 border-b border-slate-100">
                                    <Clock className="w-4 h-4 text-teal-600" />
                                    <h3 className="text-xs font-bold uppercase tracking-wider text-slate-700">1. Service &amp; Shift Configuration</h3>
                                </div>

                                {/* Preset Service Buttons */}
                                <div>
                                    <label className="block text-xs font-semibold text-slate-600 mb-1.5">Select Service Category</label>
                                    <div className="flex flex-wrap gap-1.5 mb-2">
                                        {['Old Age Care', 'New Born Baby Care', 'Japa Maid / Mother Care', 'Patient Care', 'Nursing Services'].map(cat => (
                                            <button
                                                key={cat}
                                                type="button"
                                                onClick={() => handleSelectRestartServiceCategory(cat)}
                                                className={`px-2.5 py-1 text-xs rounded-lg font-semibold border transition-all ${
                                                    restartServiceType === cat
                                                        ? 'bg-teal-500 text-white border-teal-500 shadow-xs'
                                                        : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                                                }`}
                                            >
                                                {cat}
                                            </button>
                                        ))}
                                    </div>
                                    <input
                                        type="text"
                                        value={restartServiceType}
                                        onChange={e => handleSelectRestartServiceCategory(e.target.value)}
                                        placeholder="Or type custom service name..."
                                        className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-[#1AA6A8]/30 focus:border-[#1AA6A8]"
                                    />
                                </div>

                                {/* Shift Hours & Start Date */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-xs font-semibold text-slate-600 mb-1.5">Shift Duration</label>
                                        <div className="grid grid-cols-2 gap-2">
                                            {[
                                                { h: 10, label: '10-Hour Shift' },
                                                { h: 24, label: '24-Hour Live-in' }
                                            ].map(s => (
                                                <button
                                                    key={s.h}
                                                    type="button"
                                                    onClick={() => setRestartShiftHours(s.h)}
                                                    className={`py-2 px-2 text-center rounded-xl text-xs font-bold border transition-all ${
                                                        restartShiftHours === s.h
                                                            ? 'bg-slate-900 text-white border-slate-900 shadow-xs'
                                                            : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
                                                    }`}
                                                >
                                                    {s.label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-xs font-semibold text-slate-600 mb-1.5">Service Start Date *</label>
                                        <input
                                            type="date"
                                            value={restartStartDate}
                                            onChange={e => setRestartStartDate(e.target.value)}
                                            className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-[#1AA6A8]/30 focus:border-[#1AA6A8]"
                                        />
                                    </div>
                                </div>

                                {/* Duration: Ongoing vs Fixed End Date */}
                                <div className="bg-slate-50/70 p-3 rounded-xl border border-slate-100">
                                    <div className="flex items-center justify-between mb-2">
                                        <label className="text-xs font-bold text-slate-700">Duration Format</label>
                                        <label className="inline-flex items-center gap-1.5 cursor-pointer select-none text-xs font-bold text-teal-600 hover:opacity-80">
                                            <input
                                                type="checkbox"
                                                checked={restartIsOngoing}
                                                onChange={e => {
                                                    const checked = e.target.checked;
                                                    setRestartIsOngoing(checked);
                                                    if (checked) setRestartEndDate('');
                                                }}
                                                className="w-3.5 h-3.5 rounded border-slate-300 text-teal-600 focus:ring-teal-500 cursor-pointer"
                                            />
                                            <span>Ongoing (No End Date)</span>
                                        </label>
                                    </div>

                                    {restartIsOngoing ? (
                                        <div 
                                            onClick={() => setRestartIsOngoing(false)}
                                            className="w-full px-3 py-2 rounded-lg border border-dashed border-teal-300 bg-teal-50/50 text-teal-800 text-xs font-semibold flex items-center justify-between cursor-pointer hover:bg-teal-50 transition-colors"
                                            title="Click to specify a custom end date"
                                        >
                                            <span className="flex items-center gap-1.5">
                                                <span className="w-2 h-2 rounded-full bg-teal-500 animate-pulse"></span>
                                                Active ongoing service with automatic monthly billing cycles
                                            </span>
                                            <span className="text-[10px] font-bold uppercase tracking-wider bg-white border border-teal-200 px-2 py-0.5 rounded text-teal-700">
                                                Ongoing
                                            </span>
                                        </div>
                                    ) : (
                                        <div>
                                            <label className="block text-[11px] text-slate-500 font-semibold mb-1">Service End Date</label>
                                            <input
                                                type="date"
                                                value={restartEndDate}
                                                onChange={e => setRestartEndDate(e.target.value)}
                                                className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm font-semibold bg-white focus:outline-none focus:ring-2 focus:ring-[#1AA6A8]/30"
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* 2. Service Quoting & Daily Rates */}
                            <div className="space-y-3">
                                <div className="flex items-center gap-2 pb-1.5 border-b border-slate-100">
                                    <Wallet className="w-4 h-4 text-indigo-600" />
                                    <h3 className="text-xs font-bold uppercase tracking-wider text-slate-700">2. Service Quoting &amp; Daily Rates</h3>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <div className="p-3.5 rounded-xl border border-indigo-100 bg-indigo-50/30">
                                        <div className="flex items-center justify-between mb-1">
                                            <label className="text-xs font-bold text-slate-800">Complete Month Rate *</label>
                                            <span className="text-[10px] font-bold uppercase text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded">≥ 30 Days</span>
                                        </div>
                                        <div className="relative mt-1">
                                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold text-sm">₹</span>
                                            <input
                                                type="number"
                                                value={restartCompleteRate}
                                                onChange={e => setRestartCompleteRate(e.target.value === '' ? '' : Number(e.target.value))}
                                                placeholder="e.g. 800"
                                                className="w-full pl-7 pr-3 py-2 rounded-lg border border-slate-200 text-sm font-bold bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-300"
                                            />
                                        </div>
                                        <p className="text-[11px] text-slate-500 mt-1 font-medium">
                                            ≈ ₹{((Number(restartCompleteRate) || 0) * 30).toLocaleString('en-IN')}/month standard cycle
                                        </p>
                                    </div>

                                    <div className="p-3.5 rounded-xl border border-amber-100 bg-amber-50/30">
                                        <div className="flex items-center justify-between mb-1">
                                            <label className="text-xs font-bold text-slate-800">Partial Month Rate *</label>
                                            <span className="text-[10px] font-bold uppercase text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded">&lt; 30 Days</span>
                                        </div>
                                        <div className="relative mt-1">
                                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold text-sm">₹</span>
                                            <input
                                                type="number"
                                                value={restartIncompleteRate}
                                                onChange={e => setRestartIncompleteRate(e.target.value === '' ? '' : Number(e.target.value))}
                                                placeholder="e.g. 1500"
                                                className="w-full pl-7 pr-3 py-2 rounded-lg border border-slate-200 text-sm font-bold bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-amber-300"
                                            />
                                        </div>
                                        <p className="text-[11px] text-slate-500 mt-1 font-medium">
                                            Applied if care concludes before 30 days
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* 3. Security Deposit */}
                            <div className="space-y-3">
                                <div className="flex items-center gap-2 pb-1.5 border-b border-slate-100">
                                    <ShieldCheck className="w-4 h-4 text-emerald-600" />
                                    <h3 className="text-xs font-bold uppercase tracking-wider text-slate-700">3. Security Deposit Collection</h3>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-xs font-semibold text-slate-600 mb-1">Deposit Amount (₹)</label>
                                        <div className="relative">
                                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold text-sm">₹</span>
                                            <input
                                                type="number"
                                                value={restartDepositAmount}
                                                onChange={e => setRestartDepositAmount(e.target.value === '' ? '' : Number(e.target.value))}
                                                placeholder="e.g. 5000"
                                                className="w-full pl-7 pr-3 py-2 rounded-xl border border-slate-200 text-sm font-bold text-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-300"
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-xs font-semibold text-slate-600 mb-1">Deposit Status</label>
                                        <div className="grid grid-cols-2 gap-1.5">
                                            <button
                                                type="button"
                                                onClick={() => setRestartDepositStatus('collected')}
                                                className={`py-2 px-2 text-xs font-bold rounded-xl border transition-all ${
                                                    restartDepositStatus === 'collected'
                                                        ? 'bg-emerald-500 text-white border-emerald-500 shadow-xs'
                                                        : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                                                }`}
                                            >
                                                Already Collected
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setRestartDepositStatus('pending')}
                                                className={`py-2 px-2 text-xs font-bold rounded-xl border transition-all ${
                                                    restartDepositStatus === 'pending'
                                                        ? 'bg-amber-500 text-white border-amber-500 shadow-xs'
                                                        : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                                                }`}
                                            >
                                                Pending / Bill Later
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {restartDepositStatus === 'collected' && (
                                    <div className="p-3 bg-emerald-50/40 rounded-xl border border-emerald-100 flex items-center gap-3">
                                        <div className="flex-1">
                                            <label className="block text-xs font-semibold text-emerald-900 mb-1">Payment Method</label>
                                            <select
                                                value={restartDepositMethod}
                                                onChange={e => setRestartDepositMethod(e.target.value)}
                                                className="w-full px-3 py-1.5 rounded-lg border border-emerald-200 text-xs font-semibold bg-white text-slate-800"
                                            >
                                                <option value="UPI">UPI / GPay / PhonePe</option>
                                                <option value="Cash">Cash</option>
                                                <option value="Bank Transfer">Bank Transfer / NEFT</option>
                                                <option value="Cheque">Cheque</option>
                                            </select>
                                        </div>
                                        <div className="text-xs text-emerald-800 font-medium pt-4">
                                            ₹{(Number(restartDepositAmount) || 0).toLocaleString('en-IN')} will be recorded in deposit ledger
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* 4. Patient Care Requirements & Work Form Scope */}
                            <div className="space-y-3">
                                <div className="flex items-center justify-between pb-1.5 border-b border-slate-100">
                                    <div className="flex items-center gap-2">
                                        <HeartPulse className="w-4 h-4 text-rose-500" />
                                        <h3 className="text-xs font-bold uppercase tracking-wider text-slate-700">4. Patient Care &amp; Work Form Details</h3>
                                    </div>
                                    <div className="flex items-center gap-1.5">
                                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${
                                            restartFormType === 'baby_care_form'
                                                ? 'bg-pink-50 text-pink-700 border-pink-200'
                                                : 'bg-rose-50 text-rose-700 border-rose-200'
                                        }`}>
                                            {restartFormType === 'baby_care_form' ? '🍼 Baby Care' : '🩺 Patient Care'}
                                        </span>
                                        <span className="text-[10px] font-bold text-slate-600 bg-slate-100 border border-slate-200 px-2 py-0.5 rounded-full">
                                            {restartDuties.length} {restartDuties.length === 1 ? 'duty' : 'duties'}
                                        </span>
                                    </div>
                                </div>

                                {/* Direct WhatsApp Form Dispatch Buttons */}
                                <div className="p-3 bg-gradient-to-r from-emerald-50/80 via-teal-50/50 to-slate-50 rounded-xl border border-emerald-200/80 space-y-2">
                                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                                        <div>
                                            <p className="text-xs font-bold text-emerald-950 flex items-center gap-1.5">
                                                <MessageSquare className="w-3.5 h-3.5 text-emerald-600" />
                                                Send Forms Directly to Client via WhatsApp
                                            </p>
                                            <p className="text-[11px] text-emerald-700/90 font-medium">
                                                Dispatches prefilled interactive WhatsApp forms directly to <strong>{canViewPhoneNumbers ? (restartContactNumber || restartModal.phone || 'client') : maskPhoneNumber(restartContactNumber || restartModal.phone, false)}</strong>
                                            </p>
                                        </div>
                                        <div className="flex items-center gap-1.5 shrink-0">
                                            <button
                                                type="button"
                                                onClick={handleSendConsentWhatsApp}
                                                disabled={isSendingConsent}
                                                className="px-2.5 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] font-bold flex items-center gap-1 shadow-2xs transition-all disabled:opacity-50"
                                                title="Send interactive Consent & Intake form via WhatsApp"
                                            >
                                                {isSendingConsent ? <RotateCcw className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                                                Send Consent Form
                                            </button>
                                            <button
                                                type="button"
                                                onClick={handleSendWorkFormWhatsApp}
                                                disabled={isSendingWorkForm}
                                                className="px-2.5 py-1.5 rounded-lg bg-teal-600 hover:bg-teal-700 text-white text-[11px] font-bold flex items-center gap-1 shadow-2xs transition-all disabled:opacity-50"
                                                title="Send caregiver duties checklist form via WhatsApp"
                                            >
                                                {isSendingWorkForm ? <RotateCcw className="w-3 h-3 animate-spin" /> : <ClipboardList className="w-3 h-3" />}
                                                Send Work Form
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {/* Care Summary & Preview Card */}
                                <div className="bg-slate-50/80 rounded-xl border border-slate-200/80 p-3 space-y-2.5">
                                    <div className="flex items-center justify-between">
                                        <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                                            Current Care Scope Preview
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => setIsManualCareExpanded(prev => !prev)}
                                            className="text-xs font-bold text-teal-700 hover:text-teal-800 flex items-center gap-1 bg-white border border-teal-200 hover:border-teal-300 px-2.5 py-1 rounded-lg shadow-2xs transition-all"
                                        >
                                            {isManualCareExpanded ? (
                                                <>
                                                    <ChevronUp className="w-3.5 h-3.5" />
                                                    Hide Manual Edit
                                                </>
                                            ) : (
                                                <>
                                                    <Sparkles className="w-3.5 h-3.5 text-teal-600" />
                                                    Add / Edit Details Manually
                                                </>
                                            )}
                                        </button>
                                    </div>

                                    {/* Compact Grid Summary */}
                                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                                        <div className="bg-white p-2 rounded-lg border border-slate-200/60">
                                            <span className="text-[10px] text-slate-400 font-semibold block">Patient / Baby</span>
                                            <span className="font-bold text-slate-800 truncate block">{restartPatientName || restartModal.name}</span>
                                        </div>
                                        <div className="bg-white p-2 rounded-lg border border-slate-200/60 shadow-2xs">
                                            <span className="text-[10px] text-slate-400 font-semibold uppercase tracking-wider block mb-0.5">Age &amp; Weight</span>
                                            <span className="font-bold text-slate-800 block">
                                                {renderAgeWeightPreview(restartAge, restartAgeUnit, restartWeight)}
                                            </span>
                                        </div>
                                        <div className="bg-white p-2 rounded-lg border border-slate-200/60">
                                            <span className="text-[10px] text-slate-400 font-semibold block">Guardian</span>
                                            <span className="font-bold text-slate-800 truncate block">{restartRelativeName || restartModal.name}</span>
                                        </div>
                                        <div className="bg-white p-2 rounded-lg border border-slate-200/60">
                                            <span className="text-[10px] text-slate-400 font-semibold block">Contact</span>
                                            <span className="font-bold text-slate-800 truncate block">{canViewPhoneNumbers ? (restartContactNumber || restartModal.phone || 'N/A') : maskPhoneNumber(restartContactNumber || restartModal.phone, false)}</span>
                                        </div>
                                    </div>

                                    {/* Address & Medical Condition preview */}
                                    <div className="space-y-1 text-xs">
                                        {restartAddress && (
                                            <div className="flex items-start gap-1.5 text-slate-600 bg-white p-2 rounded-lg border border-slate-200/60">
                                                <MapPin className="w-3.5 h-3.5 text-slate-400 shrink-0 mt-0.5" />
                                                <span className="line-clamp-1 font-medium">{restartAddress}</span>
                                            </div>
                                        )}
                                        {restartMedicalCondition && (
                                            <div className="flex items-start gap-1.5 text-rose-700 bg-rose-50/60 p-2 rounded-lg border border-rose-100">
                                                <Info className="w-3.5 h-3.5 text-rose-500 shrink-0 mt-0.5" />
                                                <span className="line-clamp-1 font-medium">Condition: {restartMedicalCondition}</span>
                                            </div>
                                        )}
                                    </div>

                                    {/* Selected duties pills */}
                                    <div className="pt-1">
                                        <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider block mb-1">
                                            Caregiver Work Duties ({restartDuties.length}):
                                        </span>
                                        {restartDuties.length > 0 ? (
                                            <div className="flex flex-wrap gap-1">
                                                {restartDuties.map(dKey => (
                                                    <span
                                                        key={dKey}
                                                        className="inline-flex items-center gap-1 px-2 py-0.5 bg-white border border-slate-200 text-slate-700 text-[10px] font-semibold rounded-md shadow-2xs"
                                                    >
                                                        <Check className="w-2.5 h-2.5 text-emerald-600 shrink-0" />
                                                        {getRestartDutyLabel(dKey, restartFormType)}
                                                    </span>
                                                ))}
                                            </div>
                                        ) : (
                                            <p className="text-[11px] text-amber-700 bg-amber-50/80 border border-amber-200 rounded-lg p-2 font-medium">
                                                ⚠️ No specific duties recorded yet for this service. Click <strong>"Add / Edit Details Manually"</strong> above to select the caregiver duty scope.
                                            </p>
                                        )}
                                        {restartCareInstructions && (
                                            <p className="text-[11px] text-slate-600 mt-1.5 bg-white p-2 rounded-lg border border-slate-200/60 italic">
                                                Note: "{restartCareInstructions}"
                                            </p>
                                        )}
                                    </div>
                                </div>

                                {/* Expandable Manual Form */}
                                {isManualCareExpanded && (
                                    <div className="p-4 bg-white rounded-xl border-2 border-teal-200 space-y-4 shadow-sm animate-in slide-in-from-top-2 duration-150">
                                        <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-2 border-b border-slate-100 gap-2">
                                            <span className="text-xs font-bold text-slate-800 flex items-center gap-1.5">
                                                <Sparkles className="w-3.5 h-3.5 text-teal-600" />
                                                Manual Patient Care &amp; Duty Configuration
                                            </span>
                                            <div className="flex items-center gap-2">
                                                <div className="flex items-center gap-1 bg-slate-100 p-0.5 rounded-lg border border-slate-200">
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setRestartFormType('patient_care_form');
                                                            if (restartAgeUnit === 'Months' || restartAgeUnit === 'Days') setRestartAgeUnit('Years');
                                                        }}
                                                        className={`px-2 py-1 text-[11px] font-bold rounded-md transition-all ${
                                                            restartFormType === 'patient_care_form'
                                                                ? 'bg-white text-slate-900 shadow-2xs'
                                                                : 'text-slate-500 hover:text-slate-700'
                                                        }`}
                                                    >
                                                        Patient Care
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setRestartFormType('baby_care_form');
                                                            if (restartAgeUnit === 'Years') setRestartAgeUnit('Months');
                                                        }}
                                                        className={`px-2 py-1 text-[11px] font-bold rounded-md transition-all ${
                                                            restartFormType === 'baby_care_form'
                                                                ? 'bg-white text-pink-700 shadow-2xs'
                                                                : 'text-slate-500 hover:text-slate-700'
                                                        }`}
                                                    >
                                                        Baby Care
                                                    </button>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={handleSaveCareDetails}
                                                    disabled={isSavingCareDetails}
                                                    className="px-2.5 py-1 bg-teal-600 hover:bg-teal-700 active:scale-95 text-white text-[11px] font-bold rounded-lg shadow-2xs flex items-center gap-1 transition-all disabled:opacity-50"
                                                    title="Save patient care details and caregiver duties to client record"
                                                >
                                                    {isSavingCareDetails ? (
                                                        <>
                                                            <Loader2 className="w-3 h-3 animate-spin" />
                                                            Saving...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <Save className="w-3 h-3" />
                                                            Save Details
                                                        </>
                                                    )}
                                                </button>
                                            </div>
                                        </div>

                                        {/* Patient & Consent Info Fields */}
                                        <div className="space-y-2.5">
                                            <h4 className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                                                A. Patient &amp; Consent Information
                                            </h4>
                                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                                                <div>
                                                    <label className="block text-[11px] font-semibold text-slate-600 mb-1">
                                                        {restartFormType === 'baby_care_form' ? "Baby's Name" : "Patient's Name"} *
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={restartPatientName}
                                                        onChange={e => setRestartPatientName(e.target.value)}
                                                        placeholder="e.g. Ramesh Bhai / Baby of Tina"
                                                        className="w-full px-2.5 py-1.5 rounded-lg border border-slate-200 text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-teal-500/30"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-[11px] font-semibold text-slate-600 mb-1">Age</label>
                                                    <div className="flex gap-1">
                                                        <input
                                                            type="text"
                                                            value={restartAge}
                                                            onChange={e => handleAgeInputChange(e.target.value)}
                                                            placeholder={restartFormType === 'baby_care_form' ? "e.g. 15 or 3" : "e.g. 68"}
                                                            className="w-full px-2.5 py-1.5 rounded-lg border border-slate-200 text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-teal-500/30"
                                                        />
                                                        <select
                                                            value={restartAgeUnit}
                                                            onChange={e => setRestartAgeUnit(e.target.value as 'Days' | 'Months' | 'Years')}
                                                            className="px-2 py-1.5 rounded-lg border border-slate-200 text-xs font-semibold bg-slate-50 focus:outline-none focus:ring-2 focus:ring-teal-500/30"
                                                        >
                                                            <option value="Days">Days</option>
                                                            <option value="Months">Months</option>
                                                            <option value="Years">Years</option>
                                                        </select>
                                                    </div>
                                                </div>
                                                <div>
                                                    <label className="block text-[11px] font-semibold text-slate-600 mb-1">Weight (kg)</label>
                                                    <input
                                                        type="text"
                                                        value={restartWeight}
                                                        onChange={e => handleWeightInputChange(e.target.value)}
                                                        placeholder="e.g. 55 or 4.5"
                                                        className="w-full px-2.5 py-1.5 rounded-lg border border-slate-200 text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-teal-500/30"
                                                    />
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                                                <div>
                                                    <label className="block text-[11px] font-semibold text-slate-600 mb-1">Relative / Guardian Name</label>
                                                    <input
                                                        type="text"
                                                        value={restartRelativeName}
                                                        onChange={e => setRestartRelativeName(e.target.value)}
                                                        placeholder="e.g. Tina Kachiwala (Daughter)"
                                                        className="w-full px-2.5 py-1.5 rounded-lg border border-slate-200 text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-teal-500/30"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-[11px] font-semibold text-slate-600 mb-1">Primary Contact Phone</label>
                                                    <input
                                                        type="tel"
                                                        value={restartContactNumber}
                                                        onChange={e => setRestartContactNumber(e.target.value)}
                                                        placeholder="e.g. 9876543210"
                                                        className="w-full px-2.5 py-1.5 rounded-lg border border-slate-200 text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-teal-500/30"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-[11px] font-semibold text-slate-600 mb-1">Alternate Contact (Optional)</label>
                                                    <input
                                                        type="tel"
                                                        value={restartAltContactNumber}
                                                        onChange={e => setRestartAltContactNumber(e.target.value)}
                                                        placeholder="e.g. 9123456780"
                                                        className="w-full px-2.5 py-1.5 rounded-lg border border-slate-200 text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-teal-500/30"
                                                    />
                                                </div>
                                            </div>

                                            <div>
                                                <label className="block text-[11px] font-semibold text-slate-600 mb-1">Service Address / Location</label>
                                                <input
                                                    type="text"
                                                    value={restartAddress}
                                                    onChange={e => setRestartAddress(e.target.value)}
                                                    placeholder="Flat no., building, street address, city..."
                                                    className="w-full px-2.5 py-1.5 rounded-lg border border-slate-200 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-teal-500/30"
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-[11px] font-semibold text-slate-600 mb-1">Medical Condition &amp; Health Details</label>
                                                <textarea
                                                    rows={2}
                                                    value={restartMedicalCondition}
                                                    onChange={e => setRestartMedicalCondition(e.target.value)}
                                                    placeholder="Bedridden, paralysis, diabetic, post-surgery precautions..."
                                                    className="w-full px-2.5 py-1.5 rounded-lg border border-slate-200 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-teal-500/30"
                                                />
                                            </div>
                                        </div>

                                        {/* Duties Checklist */}
                                        <div className="space-y-2 pt-2 border-t border-slate-100">
                                            <div className="flex items-center justify-between">
                                                <h4 className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                                                    B. Caregiver Work Form Checklist ({restartFormType === 'baby_care_form' ? 'Baby Care' : 'Patient Care'})
                                                </h4>
                                                <div className="flex items-center gap-2">
                                                    <button
                                                        type="button"
                                                        onClick={selectAllRestartDuties}
                                                        className="text-[10px] font-bold text-teal-700 hover:text-teal-900"
                                                    >
                                                        Select All
                                                    </button>
                                                    <span className="text-slate-300">•</span>
                                                    <button
                                                        type="button"
                                                        onClick={clearAllRestartDuties}
                                                        className="text-[10px] font-bold text-slate-500 hover:text-red-600"
                                                    >
                                                        Clear
                                                    </button>
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                                                {Object.entries(restartFormType === 'baby_care_form' ? BABY_CARE_DUTY_LABELS : PATIENT_CARE_DUTY_LABELS).map(([dKey, dLabel]) => {
                                                    const isChecked = restartDuties.includes(dKey);
                                                    return (
                                                        <button
                                                            key={dKey}
                                                            type="button"
                                                            onClick={() => toggleRestartDuty(dKey)}
                                                            className={`p-2 rounded-lg border text-left text-xs font-medium flex items-center gap-2 transition-all ${
                                                                isChecked
                                                                    ? 'bg-teal-50 border-teal-500 text-teal-900 font-bold shadow-2xs'
                                                                    : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-50'
                                                            }`}
                                                        >
                                                            <div className={`w-4 h-4 rounded flex items-center justify-center shrink-0 text-[10px] ${
                                                                isChecked ? 'bg-teal-600 text-white' : 'border border-slate-300'
                                                            }`}>
                                                                {isChecked && <Check className="w-3 h-3 stroke-[3]" />}
                                                            </div>
                                                            <span className="truncate">{dLabel}</span>
                                                        </button>
                                                    );
                                                })}
                                            </div>

                                            <div className="pt-1">
                                                <label className="block text-[11px] font-semibold text-slate-600 mb-1">
                                                    Other Work / Special Instructions for Caregiver
                                                </label>
                                                <input
                                                    type="text"
                                                    value={restartCareInstructions}
                                                    onChange={e => setRestartCareInstructions(e.target.value)}
                                                    placeholder="e.g. Needs assistance with walker, giving medicine at 8 PM..."
                                                    className="w-full px-2.5 py-1.5 rounded-lg border border-slate-200 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-teal-500/30"
                                                />
                                            </div>

                                            <div className="pt-3 border-t border-slate-200/80 flex flex-col sm:flex-row items-center justify-between gap-2">
                                                <div className="text-[11px] text-slate-500 flex items-center gap-1">
                                                    <Sparkles className="w-3 h-3 text-teal-600" />
                                                    Saves patient details and care duty scope to client profile
                                                </div>
                                                <div className="flex items-center gap-2 self-end sm:self-auto">
                                                    <button
                                                        type="button"
                                                        onClick={() => setIsManualCareExpanded(false)}
                                                        className="px-3 py-1.5 text-xs font-semibold text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-all"
                                                    >
                                                        Done
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={handleSaveCareDetails}
                                                        disabled={isSavingCareDetails}
                                                        className="px-4 py-1.5 bg-teal-600 hover:bg-teal-700 active:scale-95 text-white text-xs font-bold rounded-lg shadow-sm flex items-center gap-1.5 transition-all disabled:opacity-50"
                                                    >
                                                        {isSavingCareDetails ? (
                                                            <>
                                                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                                                Saving Changes...
                                                            </>
                                                        ) : (
                                                            <>
                                                                <Save className="w-3.5 h-3.5" />
                                                                Save Care Details
                                                            </>
                                                        )}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* 5. Staff Member Assignment (Multi-Worker Support) */}
                            <div className="space-y-3">
                                <div className="flex items-center justify-between pb-1.5 border-b border-slate-100">
                                    <div className="flex items-center gap-2">
                                        <Users className="w-4 h-4 text-purple-600" />
                                        <h3 className="text-xs font-bold uppercase tracking-wider text-slate-700">5. Assign Staff Member(s) *</h3>
                                    </div>
                                    <span className="text-[11px] font-bold text-purple-700 bg-purple-50 px-2 py-0.5 rounded-full border border-purple-100">
                                        {restartSelectedWorkers.length} selected • {restartWorkers.filter(w => w.status === 'available').length} available
                                    </span>
                                </div>

                                {/* Worker Search */}
                                <div className="relative">
                                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                                    <input
                                        type="text"
                                        value={restartWorkerSearch}
                                        onChange={e => setRestartWorkerSearch(e.target.value)}
                                        placeholder="Search available caregivers by name or role..."
                                        className="w-full pl-9 pr-3 py-2 rounded-xl border border-slate-200 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-purple-300"
                                    />
                                </div>

                                {/* Worker List */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-48 overflow-y-auto pr-1">
                                    {restartWorkers
                                        .filter(w => {
                                            if (!restartWorkerSearch.trim()) return true;
                                            const q = restartWorkerSearch.toLowerCase();
                                            return (w.full_name || '').toLowerCase().includes(q) || (w.job_title || '').toLowerCase().includes(q);
                                        })
                                        .map(w => {
                                            const isSelected = restartSelectedWorkers.some(sw => sw.id === w.id);
                                            const isAvail = w.status === 'available';
                                            const isAssignedElsewhere = !isAvail && !isSelected;
                                            return (
                                                <button
                                                    key={w.id}
                                                    type="button"
                                                    onClick={() => toggleRestartWorker(w)}
                                                    className={`p-2.5 rounded-xl border text-left flex items-center gap-2.5 transition-all ${
                                                        isSelected
                                                            ? 'border-purple-600 bg-purple-50/70 ring-2 ring-purple-600/20 shadow-xs'
                                                            : isAssignedElsewhere
                                                                ? 'border-slate-200 bg-slate-50/60 opacity-60 hover:border-amber-300 hover:bg-amber-50/30'
                                                                : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50/70'
                                                    }`}
                                                >
                                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-xs shrink-0 ${
                                                        isSelected ? 'bg-purple-600 text-white' : isAssignedElsewhere ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-700'
                                                    }`}>
                                                        {w.full_name?.charAt(0) || 'W'}
                                                    </div>
                                                    <div className="min-w-0 flex-1">
                                                        <div className="flex items-center justify-between">
                                                            <p className="font-bold text-xs text-slate-900 truncate">{w.full_name}</p>
                                                            {isSelected ? (
                                                                 <CheckCircle2 className="w-4 h-4 text-purple-600 shrink-0" />
                                                            ) : isAssignedElsewhere ? (
                                                                <span className="text-[9px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded">Deployed</span>
                                                            ) : (
                                                                <div className="w-3.5 h-3.5 rounded-full border border-slate-300 shrink-0" />
                                                            )}
                                                        </div>
                                                        <div className="flex items-center gap-1.5 mt-0.5">
                                                            <span className="text-[10px] text-slate-500 truncate">{w.job_title || 'Caregiver'}</span>
                                                            <span className={`text-[9px] font-bold px-1.5 py-0.2 rounded-full ${
                                                                isAvail ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-800'
                                                            }`}>
                                                                {isAvail ? 'Available' : w.assigned_client ? `Deployed (${w.assigned_client})` : (w.status || 'Assigned')}
                                                            </span>
                                                        </div>
                                                        <div className="text-[10px] text-slate-400 font-medium mt-0.5">
                                                            Pay: {restartShiftHours === 24 ? `24h: ₹${w.rate_24hr || '-'}/day` : `10h: ₹${w.rate_10hr || '-'}/day`}
                                                        </div>
                                                    </div>
                                                </button>
                                            );
                                        })}
                                </div>

                                {/* Selected Workers Panel */}
                                {restartSelectedWorkers.length > 0 && (
                                    <div className="p-3 bg-purple-50/60 rounded-xl border border-purple-100 space-y-2">
                                        <div className="flex items-center justify-between text-xs">
                                            <span className="font-bold text-purple-900">
                                                Assigned Staff ({restartSelectedWorkers.length}):
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => setRestartSelectedWorkers([])}
                                                className="text-[11px] text-purple-600 hover:text-red-500 font-semibold transition-colors"
                                            >
                                                Clear all
                                            </button>
                                        </div>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                            {restartSelectedWorkers.map(w => {
                                                const dailyPay = restartShiftHours === 24
                                                    ? (w.rate_24hr || 0)
                                                    : (w.rate_10hr || 0);
                                                return (
                                                    <div key={w.id} className="p-2 bg-white rounded-lg border border-purple-200/80 flex items-center justify-between text-xs shadow-2xs">
                                                        <div className="min-w-0 flex-1 pr-2">
                                                            <p className="font-bold text-slate-900 truncate">{w.full_name}</p>
                                                            <p className="text-[10px] text-purple-700 font-semibold">
                                                                ₹{dailyPay.toLocaleString('en-IN')}/day <span className="text-slate-400 font-normal">({restartShiftHours}h rate)</span>
                                                            </p>
                                                        </div>
                                                        <button
                                                            type="button"
                                                            onClick={() => toggleRestartWorker(w)}
                                                            className="p-1 text-slate-400 hover:text-red-500 hover:bg-slate-100 rounded-lg transition-colors"
                                                            title="Remove worker"
                                                        >
                                                            <X className="w-3.5 h-3.5" />
                                                        </button>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* 6. Additional Notes & Special Instructions */}
                            <div>
                                <label className="block text-xs font-semibold text-slate-600 mb-1">6. Care Notes &amp; Special Instructions (Optional)</label>
                                <textarea
                                    rows={2}
                                    value={restartNotes}
                                    onChange={e => setRestartNotes(e.target.value)}
                                    placeholder="Doctor guidelines, shift timings, patient conditions..."
                                    className="w-full px-3 py-2 rounded-xl border border-slate-200 text-xs focus:outline-none focus:ring-2 focus:ring-[#1AA6A8]/30"
                                />
                            </div>
                        </div>

                        {/* Modal Footer */}
                        <div className="p-4 border-t border-slate-100 bg-slate-50 flex flex-col sm:flex-row items-center justify-between gap-3 shrink-0">
                            <div className="text-xs text-slate-500">
                                {restartSelectedWorkers.length > 0 ? (
                                    <span>
                                        Staff: <strong className="text-slate-800">{restartSelectedWorkers.map(w => w.full_name).join(', ')}</strong> ({restartSelectedWorkers.length} assigned) • Rate: <strong className="text-slate-800">₹{restartCompleteRate}/day</strong>
                                    </span>
                                ) : (
                                    <span className="text-amber-600 font-medium">⚠️ Please select at least one staff member to proceed</span>
                                )}
                            </div>
                            <div className="flex items-center gap-2 w-full sm:w-auto">
                                <button
                                    type="button"
                                    onClick={() => setRestartModal(null)}
                                    className="flex-1 sm:flex-none px-4 py-2.5 text-xs font-bold text-slate-600 bg-white border border-slate-200 rounded-xl hover:bg-slate-100 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={handleRestartService}
                                    disabled={isRestartSubmitting || !restartStartDate || restartSelectedWorkers.length === 0 || !restartServiceType}
                                    className="flex-1 sm:flex-none px-5 py-2.5 text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 rounded-xl transition-all shadow-sm disabled:opacity-50 flex items-center justify-center gap-2"
                                >
                                    {isRestartSubmitting ? <RotateCcw className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4" />}
                                    Confirm &amp; Restart Service
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
            {/* Remove from Pipeline / Archive Confirmation Modal (Issue 4) */}
            {removeConfirmClient && (
                <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
                        <div className="p-5 border-b border-slate-100 bg-red-50 flex items-center gap-3">
                            <div className="w-9 h-9 bg-red-100 rounded-lg flex items-center justify-center">
                                <UserMinus className="w-5 h-5 text-red-600" />
                            </div>
                            <div>
                                <h2 className="text-base font-bold text-slate-900">Archive Client?</h2>
                                <p className="text-xs text-slate-500">{removeConfirmClient.displayId ? `${removeConfirmClient.displayId} • ` : ''}{removeConfirmClient.name}</p>
                            </div>
                        </div>
                        <div className="p-5 space-y-3">
                            <p className="text-sm text-slate-600">
                                This will remove <strong>{removeConfirmClient.name}</strong> from the active CRM pipeline and mark them as <strong>Archived</strong>.
                            </p>
                            <p className="text-xs text-slate-500 bg-slate-50 p-2.5 rounded-lg border border-slate-200">
                                ℹ️ Their complete record, client ID ({removeConfirmClient.displayId || 'CL-...'}), billing history, and agreements will remain safely preserved in the Client Master Database.
                            </p>
                            <div className="flex gap-3 pt-1">
                                <button onClick={() => setRemoveConfirmClient(null)} className="flex-1 py-2.5 text-sm font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors cursor-pointer">Cancel</button>
                                <button onClick={confirmRemoveFromPipeline} className="flex-1 py-2.5 text-sm font-bold text-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors shadow-sm cursor-pointer">Archive Client</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {deleteConfirmClient && user?.role === 'admin' && (
                <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-2xl w-full max-w-sm shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
                        <div className="p-5 border-b border-slate-100 bg-red-50 flex items-center gap-3">
                            <div className="w-9 h-9 bg-red-100 rounded-lg flex items-center justify-center">
                                <Trash2 className="w-5 h-5 text-red-600" />
                            </div>
                            <div>
                                <h2 className="text-base font-bold text-slate-900">Delete Permanently?</h2>
                                <p className="text-xs text-slate-500">{deleteConfirmClient.name} will be removed completely.</p>
                            </div>
                        </div>
                        <div className="p-5 space-y-3">
                            <p className="text-sm text-slate-600">This will permanently delete <strong>{deleteConfirmClient.name}</strong> from the entire database. This action cannot be undone.</p>
                            <div className="flex gap-3 pt-1 flex-col sm:flex-row">
                                <button onClick={() => setDeleteConfirmClient(null)} className="flex-1 py-2.5 text-sm font-semibold text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors">Cancel</button>
                                {deleteConfirmClient.status !== 'Trash' && (
                                    <button onClick={confirmTemporaryDelete} className="flex-1 py-2.5 text-sm font-semibold text-amber-700 bg-amber-100 hover:bg-amber-200 rounded-lg transition-colors shadow-sm">Move to Trash</button>
                                )}
                                <button onClick={confirmDeleteClient} className="flex-1 py-2.5 text-sm font-bold text-white bg-red-500 hover:bg-red-600 rounded-lg transition-colors shadow-sm">Delete Forever</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Non-Admin Deletion Request Modal */}
            {requestDeleteClient && (
                <RequestDeletionModal
                    isOpen={Boolean(requestDeleteClient)}
                    onClose={() => setRequestDeleteClient(null)}
                    entityType="client"
                    entityId={requestDeleteClient.id}
                    entityName={requestDeleteClient.name || 'Client'}
                    defaultActionType={requestDeleteClient.status === 'Trash' ? 'permanent_delete' : 'move_to_trash'}
                />
            )}

            {/* Deposit Invoice & Payment Modal */}
            {depositModal && (
                <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-3 sm:p-4 z-50 animate-in fade-in duration-150">
                    <div className="bg-white rounded-3xl w-full max-w-xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh] animate-in zoom-in-95 duration-150 border border-slate-100">
                        {/* Header */}
                        <div className="p-4 sm:p-5 border-b border-slate-100 bg-gradient-to-r from-amber-500/10 via-amber-500/5 to-white flex items-center justify-between shrink-0">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-2xl bg-amber-500 text-white flex items-center justify-center shadow-xs">
                                    <Receipt className="w-5 h-5" />
                                </div>
                                <div>
                                    <h2 className="text-base font-bold text-slate-900">Security Deposit Management</h2>
                                    <p className="text-xs text-slate-500">
                                        {depositModal.client.name} • {depositModal.client.phone || 'No Phone'}
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={() => setDepositModal(null)}
                                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-xl transition-colors cursor-pointer"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Modal Body */}
                        <div className="p-4 sm:p-6 overflow-y-auto space-y-5">
                            {/* Summary Card */}
                            <div className="p-4 bg-slate-50 rounded-2xl border border-slate-100 flex items-center justify-between">
                                <div>
                                    <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                                        {depositModal.hasActiveService ? 'Current Service & Deposit' : 'Latest Service (Ended)'}
                                    </span>
                                    <p className="font-bold text-sm text-slate-800">{depositModal.serviceName}</p>
                                    <span className="text-xs text-slate-500">
                                        {depositModal.hasActiveService ? `Start Date: ${formatDateDisplay(depositModal.startDate)}` : `Service Ended • Settled on ${formatDateDisplay(depositModal.startDate)}`}
                                    </span>
                                </div>
                                <div className="text-right">
                                    <span className={`text-[10px] font-bold uppercase tracking-wider ${
                                        depositModal.hasActiveService
                                            ? (depositModal.depositStatus === 'collected' ? 'text-emerald-600' : 'text-amber-600')
                                            : 'text-slate-600'
                                    }`}>
                                        {depositModal.hasActiveService
                                            ? (depositModal.depositStatus === 'collected' ? 'Current Deposit (Collected)' : 'Current Deposit (Pending)')
                                            : 'Deposit Status'}
                                    </span>
                                    <p className={`text-xl font-extrabold ${
                                        depositModal.hasActiveService
                                            ? (depositModal.depositStatus === 'collected' ? 'text-emerald-700' : 'text-amber-700')
                                            : 'text-slate-800'
                                    }`}>
                                        {depositModal.hasActiveService
                                            ? `₹${depositModal.depositAmount.toLocaleString('en-IN')}`
                                            : 'All Settled'}
                                    </p>
                                </div>
                            </div>

                            {/* Service Cycles & Previous Deposit Breakdown */}
                            <div className="bg-slate-50/80 rounded-2xl border border-slate-200/90 p-4 space-y-3">
                                <div className="flex items-center justify-between border-b border-slate-200/80 pb-2">
                                    <h3 className="text-xs font-bold uppercase tracking-wider text-slate-700 flex items-center gap-1.5">
                                        <HistoryIcon className="w-3.5 h-3.5 text-primary" /> Service Cycles &amp; Deposit History
                                    </h3>
                                    <span className="text-[11px] font-semibold text-slate-500">
                                        {depositModal.allCycles?.length || 1} Cycle{(depositModal.allCycles?.length || 1) > 1 ? 's' : ''}
                                    </span>
                                </div>

                                <div className="space-y-2">
                                    {depositModal.allCycles && depositModal.allCycles.length > 0 ? (
                                        depositModal.allCycles.map((cycle: any, idx: number) => (
                                            <div
                                                key={cycle.id || idx}
                                                className={`p-3 rounded-xl border flex items-center justify-between text-xs transition-all ${
                                                    cycle.isCurrent
                                                        ? 'bg-white border-amber-200 shadow-2xs'
                                                        : 'bg-slate-100/70 border-slate-200 opacity-90'
                                                }`}
                                            >
                                                <div>
                                                    <div className="flex items-center gap-2">
                                                        <span className="font-bold text-slate-800">
                                                            {cycle.serviceType}
                                                        </span>
                                                        {cycle.isCurrent ? (
                                                            <span className="px-1.5 py-0.2 rounded text-[9px] font-bold bg-amber-100 text-amber-800 border border-amber-200 uppercase">
                                                                Current Service
                                                            </span>
                                                        ) : (
                                                            <span className="px-1.5 py-0.2 rounded text-[9px] font-bold bg-slate-200 text-slate-600 uppercase">
                                                                Previous Service (Ended)
                                                            </span>
                                                        )}
                                                    </div>
                                                    <p className="text-[11px] text-slate-500 mt-0.5">
                                                        {formatDateDisplay(cycle.startDate) || 'Start'} {cycle.isCurrent 
                                                            ? (cycle.endDate ? `to ${formatDateDisplay(cycle.endDate)}` : 'to Ongoing') 
                                                            : (cycle.endDate 
                                                                ? `to ${formatDateDisplay(cycle.endDate)}` 
                                                                : (cycle.settledDate ? `to ${formatDateDisplay(cycle.settledDate)}` : '• Ended'))}
                                                    </p>
                                                </div>
                                                <div className="text-right">
                                                    <span className="font-extrabold text-sm text-slate-900">
                                                        ₹{Number(cycle.depositAmount || 0).toLocaleString('en-IN')}
                                                    </span>
                                                    <p className={`text-[10px] font-bold uppercase tracking-wide mt-0.5 ${
                                                        cycle.isCurrent
                                                            ? (depositModal.depositStatus === 'collected' || cycle.depositStatus === 'collected' ? 'text-emerald-700' : 'text-amber-700')
                                                            : 'text-slate-500'
                                                    }`}>
                                                        {cycle.isCurrent
                                                            ? (depositModal.depositStatus === 'collected' || cycle.depositStatus === 'collected' ? '✓ Paid' : '⏳ Pending')
                                                            : '✓ Settled on End'}
                                                    </p>
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="p-3 rounded-xl bg-white border border-slate-200 flex items-center justify-between text-xs">
                                            <span className="font-bold text-slate-800">{depositModal.serviceName}</span>
                                            <span className="font-extrabold text-slate-900">₹{depositModal.depositAmount.toLocaleString('en-IN')}</span>
                                        </div>
                                    )}
                                </div>

                                {depositModal.pastDepositTotal > 0 && !depositModal.allCycles?.some((c: any) => !c.isCurrent && Number(c.depositAmount) > 0) && (() => {
                                    const { settledDateStr } = getClientDepositBreakdown(depositModal.client);
                                    return (
                                        <div className="pt-2 border-t border-slate-200 flex items-center justify-between text-xs bg-white p-2.5 rounded-xl border border-slate-200 shadow-2xs">
                                            <div className="flex flex-col">
                                                <span className="font-bold text-slate-800">Refunded / Settled Deposit:</span>
                                                <span className="text-[10px] text-slate-500">
                                                    {settledDateStr ? `Settled on ${settledDateStr} upon service completion` : 'Settled upon service completion'}
                                                </span>
                                            </div>
                                            <span className="font-extrabold text-slate-800 text-sm">
                                                ₹{depositModal.pastDepositTotal.toLocaleString('en-IN')}
                                            </span>
                                        </div>
                                    );
                                })()}
                            </div>

                            {/* Section 1 & 2 or All Settled Banner */}
                            {(!depositModal.hasActiveService || depositModal.depositStatus === 'settled') ? (
                                <div className="pt-2 border-t border-slate-100">
                                    <div className="p-4 bg-slate-100/90 border border-slate-200/90 rounded-2xl flex items-center justify-between gap-3">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 rounded-xl bg-slate-700 text-white flex items-center justify-center shadow-xs shrink-0">
                                                <CheckCircle2 className="w-5 h-5" />
                                            </div>
                                            <div>
                                                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-900">
                                                    All Service Cycles Ended &amp; Deposits Settled
                                                </h4>
                                                <p className="text-xs text-slate-600 mt-0.5">
                                                    This client has no active service running. All historical security deposits ({depositModal.pastDepositTotal > 0 ? `₹${depositModal.pastDepositTotal.toLocaleString('en-IN')}` : 'recorded'}) have already been settled upon service completion.
                                                </p>
                                            </div>
                                        </div>
                                        <span className="px-2.5 py-1 rounded-full text-[10px] font-bold bg-slate-200 text-slate-800 border border-slate-300 uppercase shrink-0">
                                            ✓ All Settled
                                        </span>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    {/* Section 1: Share Deposit Invoice */}
                                    <div className="space-y-3">
                                        <div className="flex items-center gap-2 pb-1 border-b border-slate-100">
                                            <Send className="w-4 h-4 text-emerald-600" />
                                            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-700">1. Share Deposit Invoice</h3>
                                        </div>

                                        <p className="text-xs text-slate-500">
                                            Send payment request directly to the client via WhatsApp, or generate an official PDF invoice.
                                        </p>

                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                            <button
                                                type="button"
                                                onClick={() => handleShareDepositWhatsApp(
                                                    depositModal.client,
                                                    depositModal.depositAmount,
                                                    depositModal.serviceName,
                                                    depositModal.startDate,
                                                    generatedInvoiceUrl || undefined
                                                )}
                                                className="p-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold flex items-center justify-center gap-2 shadow-xs transition-colors cursor-pointer"
                                            >
                                                <MessageSquare className="w-4 h-4" /> Send on WhatsApp
                                            </button>

                                            <button
                                                type="button"
                                                onClick={handleGenerateDepositPdf}
                                                disabled={isGeneratingPdf}
                                                className="p-3 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 rounded-xl text-xs font-bold flex items-center justify-center gap-2 shadow-2xs transition-colors disabled:opacity-50 cursor-pointer"
                                            >
                                                {isGeneratingPdf ? (
                                                    <RotateCcw className="w-4 h-4 animate-spin text-slate-500" />
                                                ) : (
                                                    <Download className="w-4 h-4 text-slate-600" />
                                                )}
                                                {generatedInvoiceUrl ? 'Regenerate PDF' : 'Generate PDF Invoice'}
                                            </button>
                                        </div>

                                        {generatedInvoiceUrl && (
                                            <div className="p-3 bg-emerald-50/70 border border-emerald-200 rounded-xl flex items-center justify-between text-xs">
                                                <div className="flex items-center gap-2 min-w-0 flex-1 pr-2">
                                                    <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                                                    <span className="font-semibold text-emerald-900 truncate">Invoice PDF Ready</span>
                                                </div>
                                                <div className="flex items-center gap-2 shrink-0">
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            navigator.clipboard.writeText(generatedInvoiceUrl);
                                                            setCopiedInvoiceLink(true);
                                                            setTimeout(() => setCopiedInvoiceLink(false), 2000);
                                                        }}
                                                        className="px-2.5 py-1 bg-white border border-emerald-300 rounded-lg text-[11px] font-bold text-emerald-800 hover:bg-emerald-100 transition-colors flex items-center gap-1 cursor-pointer"
                                                    >
                                                        {copiedInvoiceLink ? <Check className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3" />}
                                                        {copiedInvoiceLink ? 'Copied' : 'Copy Link'}
                                                    </button>
                                                    <a
                                                        href={generatedInvoiceUrl}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="px-2.5 py-1 bg-emerald-600 rounded-lg text-[11px] font-bold text-white hover:bg-emerald-700 transition-colors flex items-center gap-1"
                                                    >
                                                        <ExternalLink className="w-3 h-3" /> View
                                                    </a>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Section 2: Record Payment Received (Only if pending / not yet collected) */}
                                    {depositModal.depositStatus === 'collected' ? (
                                        <div className="pt-2 border-t border-slate-100">
                                            <div className="p-4 bg-emerald-50/80 border border-emerald-200/90 rounded-2xl flex items-center justify-between gap-3">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-10 h-10 rounded-xl bg-emerald-500 text-white flex items-center justify-center shadow-xs shrink-0">
                                                        <CheckCircle2 className="w-5 h-5" />
                                                    </div>
                                                    <div>
                                                        <h4 className="text-xs font-bold uppercase tracking-wider text-emerald-900">
                                                            Security Deposit Already Collected
                                                        </h4>
                                                        <p className="text-xs text-emerald-700 mt-0.5">
                                                            ₹{depositModal.depositAmount.toLocaleString('en-IN')} has been collected and is securely held in reserve for {depositModal.serviceName}.
                                                        </p>
                                                    </div>
                                                </div>
                                                <span className="px-2.5 py-1 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-300 uppercase shrink-0">
                                                    ✓ In Reserve
                                                </span>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="space-y-3 pt-2 border-t border-slate-100">
                                            <div className="flex items-center gap-2 pb-1 border-b border-slate-100">
                                                <Wallet className="w-4 h-4 text-blue-600" />
                                                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-700">2. Record Payment Received</h3>
                                            </div>

                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                <div>
                                                    <label className="block text-xs font-semibold text-slate-600 mb-1 flex items-center justify-between">
                                                        <span>Amount Received (₹) *</span>
                                                        <span className="text-[10px] font-bold text-amber-700 bg-amber-100/80 px-1.5 py-0.2 rounded border border-amber-200 uppercase">
                                                            Fixed Deposit
                                                        </span>
                                                    </label>
                                                    <div className="relative">
                                                        <input
                                                            type="text"
                                                            readOnly
                                                            disabled
                                                            value={`₹${depositModal.depositAmount.toLocaleString('en-IN')}`}
                                                            className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm font-bold text-slate-800 bg-slate-100/80 cursor-not-allowed focus:outline-none"
                                                        />
                                                    </div>
                                                </div>

                                                <div>
                                                    <label className="block text-xs font-semibold text-slate-600 mb-1">Payment Method</label>
                                                    <select
                                                        value={depositPaymentMethod}
                                                        onChange={e => {
                                                            const m = e.target.value;
                                                            setDepositPaymentMethod(m);
                                                            setDepositPaymentRef(generateDepositRef(m));
                                                        }}
                                                        className="w-full px-3 py-2 rounded-xl border border-slate-200 text-sm font-semibold text-slate-700 bg-white focus:outline-none focus:ring-2 focus:ring-blue-300"
                                                    >
                                                        <option value="UPI">UPI / GPay / PhonePe</option>
                                                        <option value="Cash">Cash</option>
                                                        <option value="Bank Transfer">Bank Transfer (IMPS / NEFT)</option>
                                                        <option value="Cheque">Cheque</option>
                                                    </select>
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                                <div>
                                                    <label className="block text-xs font-semibold text-slate-600 mb-1">Payment Received Date *</label>
                                                    <div className="relative">
                                                        <Calendar className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                                                        <input
                                                            type="date"
                                                            required
                                                            value={depositPaymentDate}
                                                            onChange={e => setDepositPaymentDate(e.target.value)}
                                                            className="w-full pl-9 pr-3 py-2 rounded-xl border border-slate-200 text-sm font-semibold text-slate-700 bg-white focus:outline-none focus:ring-2 focus:ring-blue-300 cursor-pointer"
                                                        />
                                                    </div>
                                                </div>

                                                <div>
                                                    <div className="flex items-center justify-between mb-1">
                                                        <label className="text-xs font-semibold text-slate-600">
                                                            Transaction Reference ID (Auto-Generated)
                                                        </label>
                                                        <button
                                                            type="button"
                                                            onClick={() => setDepositPaymentRef(generateDepositRef(depositPaymentMethod))}
                                                            className="text-[10px] text-blue-600 hover:text-blue-700 font-bold flex items-center gap-1 cursor-pointer"
                                                            title="Generate new reference code"
                                                        >
                                                            <RefreshCw className="w-2.5 h-2.5" /> Re-roll ID
                                                        </button>
                                                    </div>
                                                    <div className="relative flex items-center">
                                                        <input
                                                            type="text"
                                                            value={depositPaymentRef}
                                                            onChange={e => setDepositPaymentRef(e.target.value)}
                                                            className="w-full px-3 py-2 rounded-xl border border-slate-200 text-xs font-mono font-bold text-slate-800 bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-300"
                                                        />
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                navigator.clipboard.writeText(depositPaymentRef);
                                                                toast.success('Reference ID copied');
                                                            }}
                                                            className="absolute right-2 p-1 text-slate-400 hover:text-slate-600 rounded-md hover:bg-slate-200 transition-colors cursor-pointer"
                                                            title="Copy Reference ID"
                                                        >
                                                            <Copy className="w-3.5 h-3.5" />
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                            <p className="text-[10px] text-slate-400">
                                                Automatically linked to Payment Ledger, CRM Activity, and Client Billing history.
                                            </p>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>

                        {/* Footer */}
                        <div className="p-4 border-t border-slate-100 bg-slate-50 flex items-center justify-between gap-3 shrink-0">
                            <button
                                type="button"
                                onClick={() => setDepositModal(null)}
                                className="px-4 py-2.5 text-xs font-bold text-slate-600 bg-white border border-slate-200 rounded-xl hover:bg-slate-100 transition-colors cursor-pointer"
                            >
                                Close
                            </button>

                            {depositModal.hasActiveService && depositModal.depositStatus === 'pending' && (
                                <button
                                    type="button"
                                    onClick={handleRecordDepositPayment}
                                    disabled={isRecordingPayment || !depositPaymentAmount || Number(depositPaymentAmount) <= 0}
                                    className="px-5 py-2.5 text-xs font-bold text-white bg-blue-600 hover:bg-blue-700 rounded-xl transition-all shadow-sm disabled:opacity-50 flex items-center gap-2 cursor-pointer"
                                >
                                    {isRecordingPayment ? <RotateCcw className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                                    Confirm Payment Received
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
            {inspectingClient && (
                <ClientDetailsModal 
                    client={inspectingClient} 
                    onClose={() => setInspectingClient(null)} 
                    onServiceUpdated={() => fetchClients()}
                    onStartNewService={(c) => {
                        setInspectingClient(null);
                        openRestartModal(c);
                    }}
                />
            )}

            {/* Floating Bulk Action Bar */}
            {selectedClientIds.size > 0 && (
                <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 bg-slate-900/95 text-white px-4 sm:px-6 py-3 rounded-2xl shadow-2xl border border-slate-700/80 backdrop-blur-md flex items-center gap-3 sm:gap-4 animate-in slide-in-from-bottom-5 duration-200 max-w-[95vw] flex-wrap justify-between sm:justify-start">
                    <div className="flex items-center gap-2">
                        <span className="w-6 h-6 rounded-lg bg-[#1AA6A8] text-white flex items-center justify-center text-xs font-bold shadow-2xs">
                            {selectedClientIds.size}
                        </span>
                        <span className="text-xs sm:text-sm font-bold text-white whitespace-nowrap">
                            {selectedClientIds.size} client{selectedClientIds.size !== 1 ? 's' : ''} selected
                        </span>
                        <button
                            type="button"
                            onClick={handleClearSelection}
                            className="text-xs text-slate-400 hover:text-white underline ml-1 cursor-pointer"
                        >
                            Deselect
                        </button>
                    </div>

                    <div className="h-4 w-px bg-slate-700 hidden sm:block" />

                    <div className="flex items-center gap-2 flex-wrap">
                        <button
                            type="button"
                            onClick={() => handleExportCsv(clients.filter(c => selectedClientIds.has(c.id)))}
                            className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-white text-xs font-bold rounded-xl border border-slate-600 transition-all flex items-center gap-1.5 shadow-2xs cursor-pointer"
                            title="Export selected clients to CSV / Excel"
                        >
                            <Download className="w-3.5 h-3.5 text-teal-400" />
                            <span>Export CSV/Excel</span>
                        </button>

                        <button
                            type="button"
                            onClick={() => setIsBulkMessageModalOpen(true)}
                            className="px-3.5 py-1.5 bg-[#1AA6A8] hover:bg-[#15898A] text-white text-xs font-bold rounded-xl transition-all flex items-center gap-1.5 shadow-sm cursor-pointer"
                            title="Send WhatsApp or direct messages in bulk"
                        >
                            <MessageSquare className="w-3.5 h-3.5" />
                            <span>Bulk Message</span>
                        </button>

                        <button
                            type="button"
                            onClick={handleClearSelection}
                            className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors ml-1 cursor-pointer"
                            title="Clear selection"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            )}

            {/* Bulk Message Modal */}
            <BulkMessageModal
                isOpen={isBulkMessageModalOpen}
                onClose={() => setIsBulkMessageModalOpen(false)}
                clients={clients.filter(c => selectedClientIds.has(c.id))}
            />
        </div>
    );
}
